import type { Coverage } from '@/services/coverages/types';
import type { PlanResourceDetail } from '@/services/plan-resources/types';
import {
  RequiredFieldTooltip,
  RequiredTooltipProvider,
} from '@/shared/components/required-field-tooltip';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { ValidationError } from '@/shared/exceptions/validation.error';
import { useFetchZipcode } from '@/shared/hooks/use-fetch-zipcode.hook';
import { toaster } from '@/shared/ui/toaster.component';
import { formatCurrencyFromString } from '@/shared/utils';
import { formatZipcode } from '@/shared/utils/format-zipcode.util';
import {
  Button,
  Checkbox,
  Combobox,
  Dialog,
  Field,
  RadioCard,
  Select,
  Spinner,
  Text,
} from '@noorden-insurtech/alba-design-system';
import {
  Building2,
  Layers,
  Pencil,
  Plus,
  SplitSquareHorizontal,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuoteParams } from '../../context/quote-params.context';
import {
  useBusinessActivities,
  useBusinessAssistances,
  useBusinessCoverages,
  useBusinessPlanResourcesByCode,
  useFireProtectionRatingTypes,
  useTheftProtectionRatingTypes,
} from '../../hooks/business';
import {
  AssistancePackageCard,
} from '../assistance-package-card.component';
import {
  CoverageCard,
  formatCustomDeductibleLabel,
  mapDeductibleOptions,
  type CustomDeductible,
} from '../coverage-card.component';
import {
  collectNormalFlowCoverageBlockingMessages,
  getBasicCoverageLmi,
  isBusinessInterruptionCoverage,
  isDeductibleOptionValidForLmi,
  parseCoverageCurrency,
} from '../../utils/business-location-coverage-validation.util';
import { replaceActivityCodesWithNames } from '../../utils/replace-activity-codes-with-names.util';
import {
  type BusinessLocation,
  type BusinessLocationBenefit,
  type BusinessLocationCoverage,
} from './business-location';

type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type AddBusinessAddressModalProps = {
  // eslint-disable-next-line no-unused-vars
  onConfirm: (location: BusinessLocation) => void | Promise<void>;
  constructionTypeOptions: SelectOption[];
  insuredObjectOptions: SelectOption[];
  isLoading?: boolean;
  disabled?: boolean;
  /** Jornada de cotação em análise: permite valores e exibe aviso do fluxo normal. */
  skipCoverageValueValidation?: boolean;
};

/**
 * Valores editáveis de uma cobertura no modal. `customDeductible` guarda a
 * franquia personalizada (botão "Personalizar franquia" do CoverageCard) —
 * quando presente, sobrepõe `minValue`/`name` no `policyLimitDeductible` do
 * payload, mantendo `franquia` (código base do catálogo) intacta.
 */
type CoverageFormValues = {
  lmi: string;
  franquia: string;
  customDeductible?: CustomDeductible;
};

type AddBusinessAddressFormState = {
  cep: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  activityType: string;
  activityTypeLabel: string;
  constructionType: string;
  isLandmarked: boolean;
  hasBusinessInterruption: boolean;
  businessInterruptionValue: string;
  hasUnderwritingDiscountRequest: boolean;
  assistancePackage: string;
  questionnaireAnswers: {
    value: string | string[];
    label: string | string[];
  }[];
  coverageValues: Record<string, CoverageFormValues>;
};

const STATE_OPTIONS: SelectOption[] = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
];

const DEFAULT_ASSISTANCE_PLAN_NAME = 'Especial';

const normalizePlanName = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

type QuestionKind = 'fire' | 'yesno' | 'theft';

const BUSINESS_QUESTIONNAIRE_ITEMS: { question: string; kind: QuestionKind }[] =
  [
    {
      question: 'Indique os sistemas protecionais para incêndio.',
      kind: 'fire',
    },
    {
      question: 'Local de risco encontra-se em piso superior?',
      kind: 'yesno',
    },
    {
      question: 'O imóvel está localizado dentro de shopping?',
      kind: 'yesno',
    },
    {
      question: 'Indique as alarmes protecionais contra roubo.',
      kind: 'theft',
    },
  ];

const YES_NO_OPTIONS: SelectOption[] = [
  { value: 'Y', label: 'Sim' },
  { value: 'N', label: 'Não' },
];

const ASSIST_COVERAGE_CODE = 'ASSIST';
const CAPITA_COVERAGE_CODE = 'CAPITA';
// Códigos do plano que não devem ir como benefit (ex.: itens de capitalização/sorteio).
const NON_BENEFIT_PLAN_CODES = new Set(['676063', '676067']);

function isAssistanceBenefitResource(resource: PlanResourceDetail): boolean {
  const code = String(resource.code || '')
    .trim()
    .toUpperCase();
  if (!code || code === CAPITA_COVERAGE_CODE) return false;
  if (NON_BENEFIT_PLAN_CODES.has(code)) return false;
  if (!/^\d{8}$/.test(code)) return false;

  const name = (resource.name ?? '').trim().toLowerCase();
  if (!name) return false;
  if (name.includes('sorteio') || name.includes('capitaliz')) return false;

  return true;
}

function mapResourceToBenefit(
  resource: PlanResourceDetail
): BusinessLocationBenefit {
  return {
    code: String(resource.code || '').trim(),
    value: resource.defaultValue ?? 0,
    ...(resource.productElementId
      ? { productElementId: resource.productElementId }
      : {}),
  };
}

function resolveBusinessAssistCode(productCode: string): string {
  const trimmed = (productCode || '').trim();
  return trimmed ? `${trimmed}ASS` : ASSIST_COVERAGE_CODE;
}

function resolveBusinessCapitaCode(productCode: string): string {
  const trimmed = (productCode || '').trim();
  return trimmed ? `${trimmed}CAP` : CAPITA_COVERAGE_CODE;
}

function buildAssistancePayloads({
  productCode,
  planCode,
  planResources,
}: {
  productCode: string;
  planCode: string;
  planResources: PlanResourceDetail[];
}): {
  coveragesPayload: BusinessLocationCoverage[];
  benefitsPayload: BusinessLocationBenefit[];
} {
  if (
    !planCode ||
    !Array.isArray(planResources) ||
    planResources.length === 0
  ) {
    return { coveragesPayload: [], benefitsPayload: [] };
  }

  const topLevelBenefits = planResources
    .filter(isAssistanceBenefitResource)
    .map(mapResourceToBenefit);

  const assistCoverage: BusinessLocationCoverage = {
    code: resolveBusinessAssistCode(productCode),
    value: 0,
    planCode,
    benefits: topLevelBenefits,
  };

  const capitaIndex = planResources.findIndex(
    resource =>
      String(resource.code || '')
        .trim()
        .toUpperCase() === CAPITA_COVERAGE_CODE
  );

  const capitaCoverage: BusinessLocationCoverage | null =
    capitaIndex >= 0
      ? {
        code: resolveBusinessCapitaCode(productCode),
        value: 0,
        planCode,
        ...(capitaIndex + 1 < planResources.length
          ? {
            benefits: planResources
              .slice(capitaIndex + 1)
              .map(mapResourceToBenefit),
          }
          : {}),
      }
      : null;

  return {
    coveragesPayload: [
      assistCoverage,
      ...(capitaCoverage ? [capitaCoverage] : []),
    ],
    benefitsPayload: topLevelBenefits,
  };
}

function parseCurrencyLikeToNumber(value: string): number {
  return parseCoverageCurrency(value);
}

type RequiredFieldRule = {
  key: string;
  label: string;
  getValue: (
    formState: AddBusinessAddressFormState,
    extra: { selectedInsuredObjectType: string; selectedRiskValue: string }
  ) => string;
};

const REQUIRED_FIELDS: RequiredFieldRule[] = [
  { key: 'cep', label: 'CEP', getValue: f => f.cep.replace(/\D/g, '') },
  { key: 'street', label: 'Endereço', getValue: f => f.street.trim() },
  { key: 'number', label: 'Número', getValue: f => f.number.trim() },
  {
    key: 'neighborhood',
    label: 'Bairro',
    getValue: f => f.neighborhood.trim(),
  },
  { key: 'city', label: 'Cidade', getValue: f => f.city.trim() },
  { key: 'state', label: 'Estado', getValue: f => f.state.trim() },
  {
    key: 'activityType',
    label: 'Tipo de Atividade Realizada',
    getValue: f => f.activityType.trim(),
  },
  {
    key: 'constructionType',
    label: 'Tipo de Construção',
    getValue: f => f.constructionType.trim(),
  },
  {
    key: 'insuredObjectType',
    label: 'Objeto Segurado',
    getValue: (_f, extra) => extra.selectedInsuredObjectType.trim(),
  },
  {
    key: 'riskValue',
    label: 'Risco Declarado',
    getValue: (_f, extra) => extra.selectedRiskValue.replace(/\D/g, '').trim(),
  },
];

function validateQuestionnaire(
  formState: AddBusinessAddressFormState
): Record<number, string> {
  const errors: Record<number, string> = {};
  BUSINESS_QUESTIONNAIRE_ITEMS.forEach((item, index) => {
    const answer = formState.questionnaireAnswers[index];
    const isMulti = item.kind === 'fire' || item.kind === 'theft';

    const hasValue = (() => {
      if (!answer) return false;
      if (isMulti) {
        const value = Array.isArray(answer.value) ? answer.value : [];
        const label = Array.isArray(answer.label) ? answer.label : [];
        return value.length > 0 || label.length > 0;
      }
      const value = typeof answer.value === 'string' ? answer.value : '';
      const label = typeof answer.label === 'string' ? answer.label : '';
      return Boolean(value || label);
    })();

    if (!hasValue) {
      errors[index] = 'Este campo é obrigatório';
    }
  });
  return errors;
}

function validateRequiredFields(
  formState: AddBusinessAddressFormState,
  selectedInsuredObjectType: string,
  selectedRiskValue: string
): Record<string, string> {
  const fieldErrors: Record<string, string> = {};
  const extra = { selectedInsuredObjectType, selectedRiskValue };

  for (const rule of REQUIRED_FIELDS) {
    const value = rule.getValue(formState, extra);
    if (!value) {
      fieldErrors[rule.key] = `${rule.label} é obrigatório`;
    }
  }

  if (fieldErrors.cep === undefined) {
    const cleanCep = formState.cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) {
      fieldErrors.cep = 'CEP inválido';
    }
  }

  return fieldErrors;
}

function buildRegularCoveragesPayload(
  coveragesCatalog: Coverage[],
  coverageValues: Record<string, CoverageFormValues>
): BusinessLocationCoverage[] {
  return coveragesCatalog
    .map(coverage => {
      const stored = coverageValues[coverage.id];
      const value = parseCurrencyLikeToNumber(stored?.lmi ?? '');
      const deductibleOptions = mapDeductibleOptions(coverage.deductibles ?? []);
      const selectedDeductible = deductibleOptions.find(
        option => option.value === stored?.franquia
      );
      // Item completo do catálogo da franquia selecionada — resolvido pelo
      // `id`, já que o código pode se repetir entre grupos de atividade com
      // descontos diferentes. Alimenta o `policyLimitDeductible`, que o BFF
      // converte em `PolicyLimitDeductibleList` da cobertura no InsureMO — em
      // complemento ao `codeDeductible`/`Franquia`, que continua igual.
      const selectedDeductibleDetail = selectedDeductible
        ? ((coverage.deductibles ?? []).find(
            deductible => String(deductible.id) === String(selectedDeductible.id)
          ) ??
          (coverage.deductibles ?? []).find(
            deductible =>
              String(deductible.code) === String(selectedDeductible.code)
          ))
        : undefined;

      // Franquia personalizada ("Personalizar franquia"): sobrepõe o valor
      // mínimo e o nome/descrição do item estruturado. O código base
      // selecionado no catálogo é mantido em `codeDeductible`/`id`/`code`.
      const custom = stored?.customDeductible;
      const hasCustomDeductible = Boolean(
        custom &&
          custom.percentage.trim().length > 0 &&
          custom.value.trim().length > 0
      );
      const customLabel = hasCustomDeductible
        ? formatCustomDeductibleLabel(custom!)
        : '';

      return {
        code: coverage.code || coverage.id,
        value,
        ...(coverage.productElementId
          ? { productElementId: coverage.productElementId }
          : {}),
        ...(selectedDeductible
          ? { codeDeductible: selectedDeductible.code }
          : {}),
        ...(selectedDeductibleDetail
          ? {
              policyLimitDeductible: [
                {
                  id: selectedDeductibleDetail.id,
                  code: selectedDeductibleDetail.code,
                  minValue: hasCustomDeductible
                    ? parseCurrencyLikeToNumber(custom!.value)
                    : selectedDeductibleDetail.minValue,
                  name: hasCustomDeductible
                    ? customLabel
                    : selectedDeductibleDetail.name,
                  ...(hasCustomDeductible
                    ? { description: customLabel }
                    : selectedDeductibleDetail.description
                      ? { description: selectedDeductibleDetail.description }
                      : {}),
                  // O catálogo é buscado por atividade selecionada
                  // (`useBusinessCoverages(formState.activityType)`), então
                  // o grupo do item já corresponde à atividade do local.
                  ...(selectedDeductibleDetail.activityGroup != null
                    ? { activityGroup: selectedDeductibleDetail.activityGroup }
                    : {}),
                },
              ],
            }
          : {}),
      } satisfies BusinessLocationCoverage;
    })
    .filter(coverage => coverage.value > 0);
}

/**
 * Mapeia os `fieldErrors` de um 422 do attach-element para os cards de
 * cobertura correspondentes — mesmo de-para usado no residencial
 * (`step-coverages.component.tsx`): extrai o código da cobertura citado na
 * mensagem (ex.: "Valor máximo para cobertura 11802 ...") e troca códigos
 * pelo nome da cobertura no texto exibido.
 */
function mapValidationErrorToCoverageErrors(
  error: ValidationError,
  coveragesCatalog: Coverage[]
): { coverageErrors: Record<string, string>; coverageNames: string[] } {
  const coverageErrors: Record<string, string> = {};
  const coverageNames: string[] = [];

  const replaceCodesWithNames = (message: string): string => {
    let result = message;
    coveragesCatalog.forEach(coverage => {
      const code = String(coverage.code ?? '').trim();
      if (code) {
        result = result.replace(new RegExp(`\\b${code}\\b`, 'g'), coverage.name);
      }
    });
    return result;
  };

  Object.entries(error.fieldErrors ?? {}).forEach(([key, value]) => {
    const message = String(value?.message ?? '').trim();
    if (!message) return;

    const codeMatch = message.match(/cobertura\s+(\w+)/i);
    const coverageCode = codeMatch ? codeMatch[1] : null;

    const coverage = coverageCode
      ? coveragesCatalog.find(
        c => c.code === coverageCode || c.id === coverageCode
      )
      : coveragesCatalog.find(c => c.code === key || c.id === key);

    if (coverage) {
      coverageErrors[coverage.id] = replaceCodesWithNames(message);
      if (!coverageNames.includes(coverage.name)) {
        coverageNames.push(coverage.name);
      }
    }
  });

  return { coverageErrors, coverageNames };
}

/**
 * Sem o valor de Lucros Cessantes/Despesas Fixas do local, as coberturas
 * correspondentes ficam desabilitadas — valores que tenham sido preenchidos
 * antes de o campo ser esvaziado são descartados para não irem ao payload.
 */
function clearBusinessInterruptionCoverageValues(
  current: AddBusinessAddressFormState,
  coverages: Coverage[]
): AddBusinessAddressFormState {
  const lockedIds = coverages
    .filter(coverage => isBusinessInterruptionCoverage(coverage.name))
    .filter(coverage => {
      const stored = current.coverageValues[coverage.id];
      return Boolean(
        stored?.lmi?.trim() ||
          stored?.franquia?.trim() ||
          stored?.customDeductible
      );
    })
    .map(coverage => coverage.id);

  if (lockedIds.length === 0) return current;

  const nextCoverageValues = { ...current.coverageValues };
  lockedIds.forEach(id => delete nextCoverageValues[id]);
  return { ...current, coverageValues: nextCoverageValues };
}

function createEmptyState(): AddBusinessAddressFormState {
  return {
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    activityType: '',
    activityTypeLabel: '',
    constructionType: '',
    isLandmarked: false,
    hasBusinessInterruption: false,
    businessInterruptionValue: '',
    hasUnderwritingDiscountRequest: false,
    assistancePackage: '',
    questionnaireAnswers: BUSINESS_QUESTIONNAIRE_ITEMS.map(item => {
      const isMulti = item.kind === 'fire' || item.kind === 'theft';
      return isMulti
        ? { value: [] as string[], label: [] as string[] }
        : { value: '', label: '' };
    }),
    coverageValues: {},
  };
}

function SelectField({
  label,
  required = false,
  placeholder = 'Selecione',
  value,
  options,
  onValueChange,
  error,
  disabled = false,
}: {
  label: string;
  required?: boolean;
  placeholder?: string;
  value: string;
  options: SelectOption[];
  // eslint-disable-next-line no-unused-vars
  onValueChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}) {
  return (
    <RequiredFieldTooltip active={required}>
    <Field.Root invalid={Boolean(error)}>
      <Field.Label className='text-sm font-bold text-alba-black-500'>
        {label}
        {required && <span className='text-alba-error-red-500'>*</span>}
      </Field.Label>
      <Select.Root
        value={value ? [value] : []}
        onValueChange={details => onValueChange(details.value[0] || '')}
        positioning={{ sameWidth: true }}
        items={options}
        disabled={disabled}
      >
        <Select.Control>
          <Select.Trigger className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600'>
            <Select.ValueText placeholder={placeholder} />
            <Select.Indicator />
          </Select.Trigger>
        </Select.Control>
        <Select.Positioner>
          <Select.Content>
            {options.map(option => (
              <Select.Item key={option.value} item={option}>
                <Select.ItemText>{option.label}</Select.ItemText>
                <Select.ItemIndicator />
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Select.Root>
      {error && <Field.ErrorText>{error}</Field.ErrorText>}
    </Field.Root>
    </RequiredFieldTooltip>
  );
}

function BusinessInterruptionFields({
  checked,
  value,
  onCheckedChange,
  onValueChange,
}: {
  checked: boolean;
  value: string;
  // eslint-disable-next-line no-unused-vars
  onCheckedChange: (checked: boolean) => void;
  // eslint-disable-next-line no-unused-vars
  onValueChange: (value: string) => void;
}) {
  return (
    <div className='space-y-4'>
      <Checkbox.Root
        checked={checked}
        onCheckedChange={() => {}}
        onClick={() => onCheckedChange(!checked)}
        className='flex items-center gap-3'
      >
        <Checkbox.Control className='h-5 w-5 cursor-pointer'>
          <Checkbox.Indicator />
        </Checkbox.Control>
        <Checkbox.Label className='cursor-pointer text-sm text-alba-black-500'>
          Deseja contratar Lucros Cessantes ou Despesas Fixas?
        </Checkbox.Label>
      </Checkbox.Root>

      {checked && (
        <Field.Root>
          <Field.Label className='text-sm font-bold text-alba-black-500'>
            Lucros Cessantes/Despesas Fixas
          </Field.Label>
          <Field.Input
            value={value}
            onChange={e =>
              onValueChange(formatCurrencyFromString(e.target.value))
            }
            placeholder='Digite o valor'
            className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600'
          />
        </Field.Root>
      )}
    </div>
  );
}

function BusinessActivityField({
  value,
  onValueChange,
  error,
  fallbackLabel,
  disabled = false,
}: {
  value: string;
  // eslint-disable-next-line no-unused-vars
  onValueChange: (next: { value: string; label: string }) => void;
  error?: string;
  fallbackLabel?: string;
  disabled?: boolean;
}) {
  const [search, setSearch] = useState('');
  const { data: activities = [], isLoading } = useBusinessActivities({
    search,
  });

  const items = useMemo(
    () =>
      activities.map(activity => ({
        value: String(activity.code),
        label: activity.description,
      })),
    [activities]
  );

  const foundItem = items.find(item => item.value === value);
  const displayLabel = foundItem?.label ?? fallbackLabel ?? '';

  // Edição: a label inicial pode chegar igual ao código (vinda da API).
  // Quando a lista carrega e encontramos a descrição real, propagamos para o
  // formState para que o handleConfirm envie a label correta.
  const lastResolvedLabelRef = useRef<string | null>(null);
  useEffect(() => {
    if (!value || !foundItem) return;
    if (foundItem.label === fallbackLabel) {
      lastResolvedLabelRef.current = foundItem.label;
      return;
    }
    if (lastResolvedLabelRef.current === foundItem.label) return;
    lastResolvedLabelRef.current = foundItem.label;
    onValueChange({ value: foundItem.value, label: foundItem.label });
  }, [value, foundItem, fallbackLabel, onValueChange]);

  return (
    <RequiredFieldTooltip active>
    <Field.Root invalid={Boolean(error)} disabled={disabled}>
      <Field.Label className='text-sm font-bold text-alba-black-500'>
        Tipo de Atividade Realizada
        <span className='text-alba-error-red-500'>*</span>
      </Field.Label>
      <Combobox.Root
        items={items}
        value={value ? [value] : []}
        onValueChange={details => {
          const selectedValue = details.value[0] || '';
          const selectedItem = items.find(
            item => item.value === selectedValue
          );
          onValueChange({
            value: selectedValue,
            label: selectedItem?.label ?? '',
          });
        }}
        onInputValueChange={details => setSearch(details.inputValue)}
        selectionBehavior='replace'
        positioning={{ sameWidth: true }}
        disabled={disabled}
      >
        <Combobox.Control>
          <Combobox.Trigger className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600'>
            {displayLabel || 'Selecione'}
          </Combobox.Trigger>
        </Combobox.Control>
        <Combobox.Positioner>
          <Combobox.Content>
            <Combobox.Input
              placeholder='Buscar atividade...'
              autoFocus
            />
            {isLoading ? (
              <div className='flex items-center justify-center py-4'>
                <Spinner size='sm' colorScheme='blue' />
              </div>
            ) : (
              <Combobox.List>
                {items.map(item => (
                  <Combobox.Item key={item.value} item={item}>
                    <Combobox.ItemText>{item.label}</Combobox.ItemText>
                    <Combobox.ItemIndicator />
                  </Combobox.Item>
                ))}
                {items.length === 0 && search.trim().length > 0 && (
                  <Combobox.Empty />
                )}
              </Combobox.List>
            )}
          </Combobox.Content>
        </Combobox.Positioner>
      </Combobox.Root>
      {error && <Field.ErrorText>{error}</Field.ErrorText>}
    </Field.Root>
    </RequiredFieldTooltip>
  );
}

function BusinessQuestionField({
  question,
  kind,
  value,
  label,
  error,
  onValueChange,
}: {
  question: string;
  kind: QuestionKind;
  value: string | string[];
  label: string | string[];
  error?: string;
  onValueChange: (
    // eslint-disable-next-line no-unused-vars
    next: { value: string | string[]; label: string | string[] }
  ) => void;
}) {
  const isMulti = kind === 'fire' || kind === 'theft';
  const fireQuery = useFireProtectionRatingTypes({ enabled: kind === 'fire' });
  const theftQuery = useTheftProtectionRatingTypes({
    enabled: kind === 'theft',
  });

  const options: SelectOption[] = useMemo(() => {
    if (kind === 'yesno') return YES_NO_OPTIONS;
    const data = kind === 'fire' ? fireQuery.data : theftQuery.data;
    return (data ?? []).map(item => ({
      value: String(item.code),
      label: item.description,
    }));
  }, [kind, fireQuery.data, theftQuery.data]);

  // Edição: BusinessLocation armazena codes em `value`. Para retrocompatibilidade,
  // se algum item de `value` não corresponder a nenhum code conhecido mas baterem
  // como label, convertemos para o code apropriado.
  const lastResolvedRef = useRef<string | null>(null);
  useEffect(() => {
    if (options.length === 0) return;

    if (isMulti) {
      const valueArr = Array.isArray(value) ? value : [];
      if (valueArr.length === 0) return;
      const resolved = valueArr
        .map(v => {
          const byCode = options.find(o => o.value === v);
          if (byCode) return byCode;
          return options.find(o => o.label === v);
        })
        .filter((o): o is SelectOption => Boolean(o));
      if (resolved.length === 0) return;
      const codes = resolved.map(r => r.value);
      const key = codes.join('|');
      if (lastResolvedRef.current === key) return;
      lastResolvedRef.current = key;
      const same =
        valueArr.length === codes.length &&
        valueArr.every((v, i) => v === codes[i]);
      if (same) return;
      onValueChange({
        value: codes,
        label: resolved.map(r => r.label),
      });
      return;
    }

    const valueStr = typeof value === 'string' ? value : '';
    if (!valueStr) return;
    const byCode = options.find(o => o.value === valueStr);
    if (byCode) return;
    const byLabel = options.find(o => o.label === valueStr);
    if (!byLabel) return;
    if (lastResolvedRef.current === byLabel.value) return;
    lastResolvedRef.current = byLabel.value;
    onValueChange({ value: byLabel.value, label: byLabel.label });
  }, [isMulti, value, label, options, onValueChange]);

  if (isMulti) {
    const selectedValues: string[] = (() => {
      if (Array.isArray(value) && value.length > 0) return value;
      if (Array.isArray(label) && label.length > 0) {
        return label
          .map(l => options.find(o => o.label === l)?.value ?? '')
          .filter(Boolean);
      }
      return [];
    })();

    return (
      <RequiredFieldTooltip active>
      <div className='grid grid-cols-1 gap-3 lg:grid-cols-[1fr_minmax(220px,320px)] lg:items-center'>
        <Text className='text-sm text-alba-black-500'>
          {question}
          <span className='text-alba-error-red-500'>*</span>
        </Text>
        <Field.Root invalid={Boolean(error)}>
          <Select.Root
            value={selectedValues}
            onValueChange={details => {
              const selected = details.value[0] || '';
              const selectedLabel = selected
                ? (options.find(o => o.value === selected)?.label ?? '')
                : '';
              onValueChange({
                value: selected ? [selected] : [],
                label: selectedLabel ? [selectedLabel] : [],
              });
            }}
            positioning={{ sameWidth: true }}
            items={options}
          >
            <Select.Control>
              <Select.Trigger className='h-10 w-full overflow-hidden rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600 group-[[data-invalid]]:border-alba-error-red-500'>
                <Select.ValueText
                  placeholder='Selecione uma opção'
                  className='min-w-0 flex-1 truncate text-left'
                />
                <Select.Indicator />
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                {options.map(option => (
                  <Select.Item key={option.value} item={option}>
                    <Select.ItemText>{option.label}</Select.ItemText>
                    <Select.ItemIndicator />
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
          {error && <Field.ErrorText>{error}</Field.ErrorText>}
        </Field.Root>
      </div>
      </RequiredFieldTooltip>
    );
  }

  const valueStr = typeof value === 'string' ? value : '';
  const labelStr = typeof label === 'string' ? label : '';
  const displayValue =
    valueStr || options.find(o => o.label === labelStr)?.value || '';

  return (
    <RequiredFieldTooltip active>
    <div className='grid grid-cols-1 gap-3 lg:grid-cols-[1fr_minmax(220px,320px)] lg:items-center'>
      <Text className='text-sm text-alba-black-500'>
        {question}
        <span className='text-alba-error-red-500'>*</span>
      </Text>
      <SelectField
        label=''
        placeholder='Selecione uma opção'
        value={displayValue}
        options={options}
        error={error}
        onValueChange={selected => {
          const found = options.find(o => o.value === selected);
          onValueChange({
            value: selected,
            label: found?.label ?? '',
          });
        }}
      />
    </div>
    </RequiredFieldTooltip>
  );
}

export function AddBusinessAddressModal({
  onConfirm,
  constructionTypeOptions,
  insuredObjectOptions,
  isLoading = false,
  disabled = false,
  skipCoverageValueValidation = false,
}: AddBusinessAddressModalProps) {
  const { id, productCode, productId } = useQuoteParams();
  const { mutateAsync: fetchZipcode, isPending: isLoadingCep } =
    useFetchZipcode();
  const { data: assistancePlans = [], isLoading: isLoadingAssistances } =
    useBusinessAssistances(productId);
  const [open, setOpen] = useState(false);
  const dialogBodyRef = useRef<HTMLDivElement>(null);
  const [formState, setFormState] =
    useState<AddBusinessAddressFormState>(createEmptyState());
  // Mesmo queryKey do combobox de atividades (cache compartilhado) — usado
  // para trocar códigos de atividade por nome nas mensagens de validação.
  const { data: activityCatalog = [] } = useBusinessActivities();

  // O conteúdo do dialog permanece montado entre aberturas, então o scroll
  // da abertura anterior persiste. Ao abrir, volta a visualização ao topo.
  // O Ark UI só remove o `hidden` do conteúdo de forma assíncrona, então o
  // scrollTo imediato pode rodar com o elemento ainda invisível (no-op) —
  // o double requestAnimationFrame repete o reset após o conteúdo aparecer.
  useEffect(() => {
    if (!open) return;
    const resetScroll = () => dialogBodyRef.current?.scrollTo({ top: 0 });
    resetScroll();
    let innerFrame: number | undefined;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(resetScroll);
    });
    return () => {
      cancelAnimationFrame(outerFrame);
      if (innerFrame !== undefined) cancelAnimationFrame(innerFrame);
    };
  }, [open]);
  const { data: coverages = [], isLoading: isLoadingCoverages } =
    useBusinessCoverages(formState.activityType);
  const { data: planResources = [], isLoading: isLoadingPlanResources } =
    useBusinessPlanResourcesByCode(formState.assistancePackage);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [questionnaireErrors, setQuestionnaireErrors] = useState<
    Record<number, string>
  >({});
  const [hasResolvedCep, setHasResolvedCep] = useState(false);
  const [isGenericCep, setIsGenericCep] = useState(false);

  /**
   * Travar rua e bairro só faz sentido quando a consulta REALMENTE trouxe um
   * logradouro. `isGenericCep` (CEP terminado em `000`) é um palpite de que o
   * CEP não tem rua — e errado: a Praça da Sé é `01001-000` e tem. Sem olhar o
   * `formState.street`, uma resposta sem rua deixava o campo vazio, travado e
   * obrigatório ao mesmo tempo — beco sem saída para o corretor.
   */
  const canLockStreetAndNeighborhood =
    hasResolvedCep && !isGenericCep && !!String(formState.street || '').trim();
  const [isCepBlocked, setIsCepBlocked] = useState(false);
  const zipcodeLookupCacheRef = useRef<Record<string, boolean>>({});

  const assistancePlanOptions: SelectOption[] = assistancePlans.map(plan => ({
    value: plan.code,
    label: plan.name,
  }));

  useEffect(() => {
    if (formState.assistancePackage || assistancePlans.length === 0) return;
    const defaultPlan =
      assistancePlans.find(
        plan =>
          normalizePlanName(plan.name) ===
          normalizePlanName(DEFAULT_ASSISTANCE_PLAN_NAME)
      ) ?? assistancePlans[0];
    if (defaultPlan) {
      setFormState(current =>
        current.assistancePackage
          ? current
          : { ...current, assistancePackage: defaultPlan.code }
      );
    }
  }, [assistancePlans, formState.assistancePackage]);

  const [selectedInsuredObjectType, setSelectedInsuredObjectType] =
    useState('');
  const [insuredObjects, setInsuredObjects] = useState<
    { insuredObjectType: string; riskValue: string }[]
  >([]);
  const [showInsuredObjectError, setShowInsuredObjectError] = useState(false);
  const [insuredObjectsErrors, setInsuredObjectsErrors] = useState<
    Record<string, string>
  >({});

  const normalize = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const predioOption = insuredObjectOptions.find(
    o => normalize(o.label) === 'predio'
  );
  const conteudoOption = insuredObjectOptions.find(
    o => normalize(o.label) === 'conteudo'
  );
  const combinedOption = insuredObjectOptions.find(
    o => normalize(o.label) === 'predio e conteudo'
  );

  const insuredObjectCardOptions = [
    predioOption && {
      ...predioOption,
      displayLabel: 'Prédio',
      icon: Building2,
    },
    conteudoOption && {
      ...conteudoOption,
      displayLabel: 'Conteúdo',
      icon: Layers,
    },
    combinedOption && {
      ...combinedOption,
      displayLabel: 'Prédio e Conteúdo',
      icon: SplitSquareHorizontal,
    },
  ].filter(Boolean) as Array<{
    label: string;
    value: string;
    displayLabel: string;
    icon: typeof Building2;
  }>;

  const isCombinedInsuredObject = (v: string) => v === combinedOption?.value;
  const isIndividualInsuredObject = (v: string) =>
    v === predioOption?.value || v === conteudoOption?.value;

  const orderInsuredObjects = (
    rows: { insuredObjectType: string; riskValue: string }[]
  ) => {
    const order = insuredObjectCardOptions.map(option => option.value);
    return rows
      .filter(row => String(row.insuredObjectType || '').trim())
      .sort((a, b) => {
        const indexA = order.indexOf(a.insuredObjectType);
        const indexB = order.indexOf(b.insuredObjectType);
        return (
          (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) -
          (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB)
        );
      });
  };

  const selectedRiskValue =
    insuredObjects.find(
      row => row.insuredObjectType === selectedInsuredObjectType
    )?.riskValue || '';

  const updateField = <K extends keyof AddBusinessAddressFormState>(
    key: K,
    value: AddBusinessAddressFormState[K]
  ) => {
    setFormState(current => ({
      ...current,
      [key]: value,
    }));
    setErrors(current => {
      if (!current[key as string]) return current;
      const next = { ...current };
      delete next[key as string];
      return next;
    });
  };

  const updateQuestionnaireAnswer = (
    index: number,
    next: { value: string | string[]; label: string | string[] }
  ) => {
    setFormState(current => ({
      ...current,
      questionnaireAnswers: current.questionnaireAnswers.map((answer, i) =>
        i === index ? next : answer
      ),
    }));
    setQuestionnaireErrors(current => {
      if (current[index] === undefined) return current;
      const nextErrors = { ...current };
      delete nextErrors[index];
      return nextErrors;
    });
  };

  const updateCoverageField = (
    coverageId: string,
    field: 'lmi' | 'franquia',
    value: string
  ) => {
    setFormState(current => ({
      ...current,
      coverageValues: {
        ...current.coverageValues,
        [coverageId]: {
          ...current.coverageValues[coverageId],
          [field]: value,
        },
      },
    }));
  };

  const updateCoverageCustomDeductible = (
    coverageId: string,
    value: CustomDeductible | undefined
  ) => {
    setFormState(current => ({
      ...current,
      coverageValues: {
        ...current.coverageValues,
        [coverageId]: {
          ...current.coverageValues[coverageId],
          customDeductible: value,
        },
      },
    }));
  };

  // Lucros Cessantes e Despesas Fixas só podem ser preenchidas após o valor
  // de Lucros Cessantes/Despesas Fixas do local ser informado.
  const hasBusinessInterruptionValue =
    formState.hasBusinessInterruption &&
    parseCoverageCurrency(formState.businessInterruptionValue) > 0;

  useEffect(() => {
    if (hasBusinessInterruptionValue) return;
    setFormState(current =>
      clearBusinessInterruptionCoverageValues(current, coverages)
    );
  }, [hasBusinessInterruptionValue, coverages]);

  const resolveZipcode = async (
    rawCep: string
  ): Promise<{ success: boolean; blocked: boolean }> => {
    const cleanCep = rawCep.replace(/\D/g, '');
    if (cleanCep.length !== 8) return { success: false, blocked: false };

    setIsCepBlocked(false);

    try {
      const result = await fetchZipcode({
        zipcode: cleanCep,
        policyId: id,
        productCode,
      });

      if (result.success && result.data) {
        if ('blocked' in result.data && result.data.blocked) {
          setIsCepBlocked(true);
          toaster.create({
            type: 'error',
            title: 'Risco não aceito',
            description: 'CEP bloqueado para cotação.',
          });
          return { success: false, blocked: true };
        }

        if (!('street' in result.data)) {
          return { success: false, blocked: false };
        }

        zipcodeLookupCacheRef.current[cleanCep] = true;
        setHasResolvedCep(true);
        setIsGenericCep(cleanCep.endsWith('000'));
        const zipcodeData = result.data;
        setFormState(current => ({
          ...current,
          street: zipcodeData.street || '',
          neighborhood: zipcodeData.neighborhood || '',
          city: zipcodeData.city || '',
          state: zipcodeData.state || '',
        }));
        return { success: true, blocked: false };
      }

      zipcodeLookupCacheRef.current[cleanCep] = false;
      setHasResolvedCep(false);
      setIsGenericCep(false);
      toaster.create({
        type: 'error',
        title: 'CEP não encontrado',
        description: 'Verifique o CEP informado e tente novamente.',
      });
      return { success: false, blocked: false };
    } catch (error) {
      zipcodeLookupCacheRef.current[cleanCep] = false;
      setHasResolvedCep(false);
      setIsGenericCep(false);
      toaster.create({
        type: 'error',
        title: 'Erro ao buscar CEP',
        description:
          error instanceof Error && error.message.trim()
            ? error.message
            : 'Verifique o CEP informado e tente novamente.',
      });
      return { success: false, blocked: false };
    }
  };

  const [coverageErrors, setCoverageErrors] = useState<Record<string, string>>(
    {}
  );

  const resetModalState = () => {
    setFormState(createEmptyState());
    setErrors({});
    setCoverageErrors({});
    setQuestionnaireErrors({});
    setSelectedInsuredObjectType('');
    setInsuredObjects([]);
    setShowInsuredObjectError(false);
    setInsuredObjectsErrors({});
    setHasResolvedCep(false);
    setIsGenericCep(false);
    setIsCepBlocked(false);
    zipcodeLookupCacheRef.current = {};
  };

  const buildDeductibleOptionsByCoverageId = () =>
    Object.fromEntries(
      coverages.map(coverage => [
        coverage.id,
        mapDeductibleOptions(coverage.deductibles ?? []),
      ])
    );

  const validateCoverages = (): boolean => {
    if (skipCoverageValueValidation) {
      setCoverageErrors({});
      return true;
    }

    const newErrors = collectNormalFlowCoverageBlockingMessages(
      coverages,
      formState.coverageValues,
      selectedRiskValue,
      buildDeductibleOptionsByCoverageId()
    );

    setCoverageErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstError = Object.values(newErrors)[0];
      toaster.create({
        type: 'error',
        title: 'Erro nas coberturas',
        description: firstError,
      });
      return false;
    }

    return true;
  };

  const handleConfirm = async () => {
    if (isLoading || isLoadingCep || isCepBlocked) return;

    const fieldValidationErrors = validateRequiredFields(
      formState,
      selectedInsuredObjectType,
      selectedRiskValue
    );

    if (!selectedInsuredObjectType) {
      setShowInsuredObjectError(true);
    }

    const questionnaireValidationErrors = validateQuestionnaire(formState);

    if (
      Object.keys(fieldValidationErrors).length > 0 ||
      Object.keys(questionnaireValidationErrors).length > 0
    ) {
      setErrors(fieldValidationErrors);
      setQuestionnaireErrors(questionnaireValidationErrors);
      toaster.create({
        type: 'error',
        title: 'Campos obrigatórios',
        description: 'Preencha todos os campos obrigatórios para continuar.',
      });
      return;
    }

    const cleanCep = formState.cep.replace(/\D/g, '');
    if (
      cleanCep.length === 8 &&
      zipcodeLookupCacheRef.current[cleanCep] !== true
    ) {
      const { blocked } = await resolveZipcode(cleanCep);
      if (blocked) return;
    }

    if (!validateCoverages()) return;

    const activityTypeLabel =
      formState.activityTypeLabel || formState.activityType;
    const constructionTypeLabel =
      constructionTypeOptions.find(
        option => option.value === formState.constructionType
      )?.label ?? formState.constructionType;
    const insuredObjectTypeLabel =
      insuredObjectOptions.find(
        option => option.value === selectedInsuredObjectType
      )?.label ?? selectedInsuredObjectType;
    const assistancePackage =
      assistancePlans.find(plan => plan.code === formState.assistancePackage)
        ?.name ?? formState.assistancePackage;
    const questionnaireAnswers: (string | string[])[] =
      formState.questionnaireAnswers.map((answer, index) => {
        const item = BUSINESS_QUESTIONNAIRE_ITEMS[index];
        const isMulti = item?.kind === 'fire' || item?.kind === 'theft';
        if (isMulti) {
          const valueArr = Array.isArray(answer.value) ? answer.value : [];
          return valueArr;
        }
        const valueStr = typeof answer.value === 'string' ? answer.value : '';
        return valueStr;
      });

    const { coveragesPayload: assistanceCoverages, benefitsPayload } =
      buildAssistancePayloads({
        productCode,
        planCode: formState.assistancePackage,
        planResources,
      });
    const regularCoverages = buildRegularCoveragesPayload(
      coverages,
      formState.coverageValues
    );
    const coveragesPayload = [...regularCoverages, ...assistanceCoverages];

    try {
      await onConfirm({
        id: crypto.randomUUID(),
        cep: formState.cep.trim(),
        street: formState.street.trim(),
        number: formState.number.trim(),
        complement: formState.complement.trim(),
        neighborhood: formState.neighborhood.trim(),
        city: formState.city.trim(),
        state: formState.state.trim(),
        activityType: formState.activityType,
        activityTypeLabel,
        constructionType: formState.constructionType,
        constructionTypeLabel,
        isLandmarked: formState.isLandmarked,
        insuredObjectType: selectedInsuredObjectType,
        insuredObjectTypeLabel,
        riskValue: selectedRiskValue.trim(),
        businessInterruptionValue: formState.hasBusinessInterruption
          ? formState.businessInterruptionValue.trim()
          : '',
        underwritingDiscountRequestInd: formState.hasUnderwritingDiscountRequest
          ? 'Y'
          : 'N',
        assistancePackage,
        assistancePackageCode: formState.assistancePackage,
        questionnaireAnswers,
        coveragesPayload,
        benefitsPayload,
      });

      toaster.create({
        type: 'success',
        title: 'Local cadastrado',
        description: 'O local de risco foi cadastrado com sucesso.',
      });
      setOpen(false);
      resetModalState();
    } catch (error) {
      const isQuoteClosed =
        error instanceof ValidationError &&
        error.fieldErrors?.quoteStatus?.code === '2';

      if (isQuoteClosed) {
        setOpen(false);
        resetModalState();
        return;
      }

      if (error instanceof ValidationError && error.fieldErrors) {
        const { coverageErrors: mappedErrors, coverageNames } =
          mapValidationErrorToCoverageErrors(error, coverages);
        if (Object.keys(mappedErrors).length > 0) {
          setCoverageErrors(prev => ({ ...prev, ...mappedErrors }));
          toaster.create({
            type: 'error',
            title: 'Erro de validação',
            description: `Erro nas coberturas: ${coverageNames.join(', ')}`,
          });
          return;
        }
      }

      toaster.create({
        type: 'error',
        title: 'Erro ao salvar local',
        description:
          error instanceof Error
            ? replaceActivityCodesWithNames(error.message, [
                ...activityCatalog,
                // Fallback: a atividade recém-selecionada pode não estar na
                // página carregada do catálogo (busca paginada/filtrada).
                {
                  code: Number(formState.activityType),
                  description: formState.activityTypeLabel,
                },
              ])
            : 'Não foi possível salvar o local de risco.',
      });
    }
  };

  return (
    <Dialog.Root
      open={open}
      closeOnInteractOutside={false}
      closeOnEscape={false}
      onOpenChange={details => {
        setOpen(details.open);
        if (!details.open) {
          resetModalState();
        }
      }}
    >
      <Dialog.Trigger asChild>
        <Button
          variant='secondary'
          size='md'
          disabled={disabled}
          className='h-12 w-full lg:h-auto lg:w-auto'
        >
          <Plus className='h-4 w-4' strokeWidth={1.6} />
          Adicionar Local
        </Button>
      </Dialog.Trigger>

      <Dialog.Backdrop />

      <Dialog.Positioner>
        <Dialog.Content className='w-[920px] max-w-[95vw] rounded-[24px] bg-alba-white-500 p-0 shadow-xl'>
          <div className='flex max-h-[90vh] flex-col gap-8 p-6'>
            <Dialog.Header className='w-full p-0'>
              <Dialog.Title className='w-full'>
                <div className='flex w-full items-center justify-between gap-3'>
                  <div className='flex items-center gap-2'>
                    <Plus className='h-6 w-6 text-alba-black-500' />
                    <Text className='text-lg font-bold text-alba-black-500'>
                      Adicionar Local
                    </Text>
                  </div>
                  <Dialog.CloseTrigger asChild>
                    <Dialog.CloseButton />
                  </Dialog.CloseTrigger>
                </div>
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body
              ref={dialogBodyRef}
              className='w-full flex-1 overflow-y-auto p-0 pr-2'
            >
              <RequiredTooltipProvider restartKey={String(open)} introMs={4000}>
              <div className='flex flex-col gap-8'>
                <div className='space-y-4'>
                  <Text className='text-base text-alba-black-500'>
                    Dados do Local
                  </Text>

                  <div className='space-y-4'>
                    <div className='lg:w-1/2'>
                      <RequiredFieldTooltip active>
                      <Field.Root invalid={Boolean(errors.cep)}>
                        <Field.Label className='text-sm font-bold text-alba-black-500'>
                          CEP
                          <span className='text-alba-error-red-500'>*</span>
                        </Field.Label>
                        <Field.Input
                          value={formState.cep}
                          onChange={event =>
                            updateField(
                              'cep',
                              formatZipcode(event.target.value)
                            )
                          }
                          onBlur={() => void resolveZipcode(formState.cep)}
                          placeholder='XXXXX-XXX'
                          maxLength={FIELD_MAX_LENGTHS.cep}
                          className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600'
                          disabled={isLoadingCep}
                        />
                        {errors.cep && (
                          <Field.ErrorText>{errors.cep}</Field.ErrorText>
                        )}
                      </Field.Root>
                      </RequiredFieldTooltip>
                    </div>

                    <RequiredFieldTooltip active>
                    <Field.Root invalid={Boolean(errors.street)}>
                      <Field.Label className='text-sm font-bold text-alba-black-500'>
                        Endereço
                        <span className='text-alba-error-red-500'>*</span>
                      </Field.Label>
                      <Field.Input
                        value={formState.street}
                        onChange={event =>
                          updateField('street', event.target.value)
                        }
                        placeholder='Rua, Avenida ...'
                        maxLength={FIELD_MAX_LENGTHS.street}
                        className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600'
                        disabled={canLockStreetAndNeighborhood}
                      />
                      {errors.street && (
                        <Field.ErrorText>{errors.street}</Field.ErrorText>
                      )}
                    </Field.Root>
                    </RequiredFieldTooltip>

                    <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
                      <RequiredFieldTooltip active>
                      <Field.Root invalid={Boolean(errors.number)}>
                        <Field.Label className='text-sm font-bold text-alba-black-500'>
                          Número
                          <span className='text-alba-error-red-500'>*</span>
                        </Field.Label>
                        <Field.Input
                          value={formState.number}
                          onChange={event =>
                            updateField('number', event.target.value)
                          }
                          placeholder='Número'
                          maxLength={FIELD_MAX_LENGTHS.addressNumber}
                          className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600'
                        />
                        {errors.number && (
                          <Field.ErrorText>{errors.number}</Field.ErrorText>
                        )}
                      </Field.Root>
                      </RequiredFieldTooltip>

                      <Field.Root>
                        <Field.Label className='text-sm font-bold text-alba-black-500'>
                          Complemento
                        </Field.Label>
                        <Field.Input
                          value={formState.complement}
                          onChange={event =>
                            updateField('complement', event.target.value)
                          }
                          placeholder='Apto, Bloco ...'
                          maxLength={FIELD_MAX_LENGTHS.complement}
                          className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600'
                        />
                      </Field.Root>
                    </div>

                    <div className='grid grid-cols-1 gap-4 lg:grid-cols-3'>
                      <RequiredFieldTooltip active>
                      <Field.Root invalid={Boolean(errors.neighborhood)}>
                        <Field.Label className='text-sm font-bold text-alba-black-500'>
                          Bairro
                          <span className='text-alba-error-red-500'>*</span>
                        </Field.Label>
                        <Field.Input
                          value={formState.neighborhood}
                          onChange={event =>
                            updateField('neighborhood', event.target.value)
                          }
                          placeholder='Bairro'
                          maxLength={FIELD_MAX_LENGTHS.neighborhood}
                          className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600'
                          disabled={canLockStreetAndNeighborhood}
                        />
                        {errors.neighborhood && (
                          <Field.ErrorText>
                            {errors.neighborhood}
                          </Field.ErrorText>
                        )}
                      </Field.Root>
                      </RequiredFieldTooltip>

                      <RequiredFieldTooltip active>
                      <Field.Root invalid={Boolean(errors.city)}>
                        <Field.Label className='text-sm font-bold text-alba-black-500'>
                          Cidade
                          <span className='text-alba-error-red-500'>*</span>
                        </Field.Label>
                        <Field.Input
                          value={formState.city}
                          onChange={event =>
                            updateField('city', event.target.value)
                          }
                          placeholder='Cidade'
                          className='h-10 w-full rounded-xl border border-alba-gray-400 bg-[#E7E8E9] px-5 text-base text-alba-gray-600'
                          disabled
                        />
                        {errors.city && (
                          <Field.ErrorText>{errors.city}</Field.ErrorText>
                        )}
                      </Field.Root>
                      </RequiredFieldTooltip>

                      <SelectField
                        label='Estado'
                        required
                        placeholder='UF'
                        value={formState.state}
                        options={STATE_OPTIONS}
                        onValueChange={value => updateField('state', value)}
                        error={errors.state}
                        disabled
                      />
                    </div>
                  </div>
                </div>

                <div className='space-y-4'>
                  <Text className='text-base text-alba-black-500'>
                    Dados do Risco
                  </Text>

                  <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
                    <BusinessActivityField
                      value={formState.activityType}
                      onValueChange={({ value, label }) => {
                        setFormState(current => ({
                          ...current,
                          activityType: value,
                          activityTypeLabel: label,
                        }));
                        setErrors(current => {
                          if (!current.activityType) return current;
                          const next = { ...current };
                          delete next.activityType;
                          return next;
                        });
                      }}
                      fallbackLabel={formState.activityTypeLabel}
                      error={errors.activityType}
                    />

                    <SelectField
                      label='Tipo de Construção'
                      required
                      value={formState.constructionType}
                      options={constructionTypeOptions}
                      onValueChange={value =>
                        updateField('constructionType', value)
                      }
                      error={errors.constructionType}
                    />
                  </div>

                  <Checkbox.Root
                    checked={formState.isLandmarked}
                    onCheckedChange={() => {}}
                    onClick={() =>
                      updateField('isLandmarked', !formState.isLandmarked)
                    }
                    className='flex items-center gap-3'
                  >
                    <Checkbox.Control className='h-5 w-5 cursor-pointer'>
                      <Checkbox.Indicator />
                    </Checkbox.Control>
                    <Checkbox.Label className='cursor-pointer text-sm text-alba-black-500'>
                      Imóvel Tombado?
                    </Checkbox.Label>
                  </Checkbox.Root>

                  <div className='space-y-4'>
                    <RequiredFieldTooltip active>
                    <div className='space-y-4 sm:space-y-2'>
                      <label className='block text-sm font-bold text-alba-black-500'>
                        Objeto Segurado
                        <span className='text-alba-error-red-500'>*</span>
                      </label>
                      <RadioCard.Root
                        value={selectedInsuredObjectType}
                        onValueChange={details => {
                          const nextValue = String(details.value || '').trim();
                          setSelectedInsuredObjectType(nextValue);
                          setShowInsuredObjectError(false);
                          setInsuredObjectsErrors({});
                          setErrors(current => {
                            const next = { ...current };
                            delete next.insuredObjectType;
                            delete next.riskValue;
                            return next;
                          });
                          setInsuredObjects(prev => {
                            if (isCombinedInsuredObject(nextValue)) {
                              return prev.filter(
                                row =>
                                  isCombinedInsuredObject(
                                    String(row.insuredObjectType || '').trim()
                                  ) && String(row.riskValue || '').trim()
                              );
                            }
                            return orderInsuredObjects(
                              prev.filter(row =>
                                isIndividualInsuredObject(
                                  String(row.insuredObjectType || '').trim()
                                )
                              )
                            );
                          });
                        }}
                      >
                        <div className='flex flex-col gap-2 sm:flex-row'>
                          {insuredObjectCardOptions.map(option => {
                            const Icon = option.icon;
                            return (
                              <RadioCard.Item
                                key={option.value}
                                value={option.value}
                                className='h-auto w-full rounded-xl sm:h-[120px] sm:w-52'
                              >
                                <RadioCard.ItemControl className='relative flex h-full flex-col items-start justify-center px-4'>
                                  <div className='absolute right-3 top-3'>
                                    <RadioCard.RadioButton />
                                  </div>
                                  <div className='flex flex-row items-center justify-start gap-3 py-3 sm:flex-col sm:items-start sm:gap-2 sm:py-0'>
                                    <Icon
                                      className='h-6 w-6 sm:h-8 sm:w-8'
                                      strokeWidth={1.6}
                                    />
                                    <RadioCard.ItemText className='whitespace-nowrap text-left'>
                                      {option.displayLabel}
                                    </RadioCard.ItemText>
                                  </div>
                                </RadioCard.ItemControl>
                                <RadioCard.ItemHiddenInput />
                              </RadioCard.Item>
                            );
                          })}
                        </div>
                      </RadioCard.Root>
                      {showInsuredObjectError && (
                        <Field.Root invalid>
                          <Field.ErrorText>
                            Selecione o objeto segurado
                          </Field.ErrorText>
                        </Field.Root>
                      )}
                    </div>
                    </RequiredFieldTooltip>

                    {selectedInsuredObjectType && (
                      <RequiredFieldTooltip active>
                      <Field.Root
                        invalid={
                          !!insuredObjectsErrors[selectedInsuredObjectType] ||
                          !!errors.riskValue
                        }
                        required
                      >
                        <Field.Label>
                          Risco declarado
                          <span className='text-alba-error-red-500'>*</span>
                        </Field.Label>
                        <Field.Input
                          value={selectedRiskValue}
                          onChange={e => {
                            const value = formatCurrencyFromString(
                              e.target.value
                            );
                            setInsuredObjectsErrors(prev => {
                              if (!prev[selectedInsuredObjectType]) return prev;
                              const next = { ...prev };
                              delete next[selectedInsuredObjectType];
                              return next;
                            });
                            setErrors(current => {
                              if (!current.riskValue) return current;
                              const next = { ...current };
                              delete next.riskValue;
                              return next;
                            });
                            setInsuredObjects(prev => {
                              const next = prev.filter(
                                row =>
                                  row.insuredObjectType !==
                                  selectedInsuredObjectType
                              );
                              if (String(value || '').trim()) {
                                next.push({
                                  insuredObjectType: selectedInsuredObjectType,
                                  riskValue: value,
                                });
                              }
                              return orderInsuredObjects(next);
                            });
                          }}
                          placeholder='R$ 0,00'
                          className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600'
                        />
                        {(insuredObjectsErrors[selectedInsuredObjectType] ||
                          errors.riskValue) && (
                          <Field.ErrorText>
                            {insuredObjectsErrors[selectedInsuredObjectType] ||
                              errors.riskValue}
                          </Field.ErrorText>
                        )}
                      </Field.Root>
                      </RequiredFieldTooltip>
                    )}

                    <BusinessInterruptionFields
                      checked={formState.hasBusinessInterruption}
                      value={formState.businessInterruptionValue}
                      onCheckedChange={next =>
                        setFormState(current => ({
                          ...current,
                          hasBusinessInterruption: next,
                          businessInterruptionValue: next
                            ? current.businessInterruptionValue
                            : '',
                        }))
                      }
                      onValueChange={value =>
                        updateField('businessInterruptionValue', value)
                      }
                    />
                  </div>
                </div>

                <div className='space-y-4'>
                  <Text className='text-base text-alba-black-500'>
                    Coberturas
                  </Text>
                  <div className='space-y-3'>
                    {!formState.activityType ? (
                      <div className='rounded-xl border border-dashed border-alba-gray-400 bg-alba-gray-100 px-4 py-6 text-center'>
                        <Text className='text-sm text-alba-gray-600'>
                          Selecione o tipo de atividade realizada para visualizar
                          as coberturas disponíveis.
                        </Text>
                      </div>
                    ) : isLoadingCoverages ? (
                      <Text className='text-sm text-alba-gray-600'>
                        Carregando coberturas...
                      </Text>
                    ) : coverages.length === 0 ? (
                      <div className='rounded-xl border border-dashed border-alba-gray-400 bg-alba-gray-100 px-4 py-6 text-center'>
                        <Text className='text-sm text-alba-gray-600'>
                          Não há coberturas disponíveis para o tipo de atividade
                          selecionado.
                        </Text>
                      </div>
                    ) : (
                      coverages.map(coverage => (
                        <CoverageCard
                          key={coverage.id}
                          coverage={{
                            id: coverage.id,
                            code: coverage.code,
                            title: coverage.name,
                            description: coverage.description ?? '',
                            required: coverage.required,
                            limit: {
                              type: 'range',
                              constraints: {
                                min: coverage.limit?.constraints?.min ?? 0,
                                max:
                                  coverage.limit?.constraints?.max ??
                                  Number.MAX_SAFE_INTEGER,
                              },
                            },
                            limitValue:
                              formState.coverageValues[coverage.id]?.lmi ?? '',
                            deductible:
                              formState.coverageValues[coverage.id]?.franquia ??
                              '',
                            deductibleOptions: mapDeductibleOptions(
                              coverage.deductibles ?? []
                            ),
                            onLimitChange: value => {
                              updateCoverageField(coverage.id, 'lmi', value);
                              if (coverageErrors[coverage.id]) {
                                setCoverageErrors(prev => {
                                  const next = { ...prev };
                                  delete next[coverage.id];
                                  return next;
                                });
                              }
                            },
                            onDeductibleChange: value =>
                              updateCoverageField(
                                coverage.id,
                                'franquia',
                                value
                              ),
                            allowCustomDeductible: true,
                            customDeductible:
                              formState.coverageValues[coverage.id]
                                ?.customDeductible,
                            onCustomDeductibleChange: value =>
                              updateCoverageCustomDeductible(
                                coverage.id,
                                value
                              ),
                            disabled:
                              isBusinessInterruptionCoverage(coverage.name) &&
                              !hasBusinessInterruptionValue,
                            validationError: coverageErrors[coverage.id],
                            skipValueValidation: skipCoverageValueValidation,
                            riskValueLimit: parseCurrencyLikeToNumber(
                              selectedRiskValue
                            ),
                            percentOfBasicLimit: coverage.limit?.percent,
                            basicCoverageLmi: getBasicCoverageLmi(
                              coverages,
                              formState.coverageValues
                            ),
                          }}
                        />
                      ))
                    )}
                  </div>
                </div>

                <div className='space-y-4'>
                  <Text className='text-base text-alba-black-500'>
                    Assistências
                  </Text>
                  {isLoadingAssistances ? (
                    <Text className='text-sm text-alba-gray-600'>
                      Carregando pacotes...
                    </Text>
                  ) : (
                    <SelectField
                      label='Pacote'
                      placeholder='Selecione o pacote'
                      value={formState.assistancePackage}
                      options={assistancePlanOptions}
                      onValueChange={value =>
                        updateField('assistancePackage', value)
                      }
                    />
                  )}

                  {formState.assistancePackage && (
                    <div className='flex flex-col gap-3'>
                      {isLoadingPlanResources ? (
                        <Text className='text-sm text-alba-gray-600'>
                          Carregando benefícios do pacote...
                        </Text>
                      ) : (
                        planResources.length > 0 && (
                          <div className='grid grid-cols-1 gap-3 lg:grid-cols-2'>
                            {planResources
                              .filter(isAssistanceBenefitResource)
                              .map(resource => (
                                <AssistancePackageCard
                                  key={resource.code}
                                  title={resource.name}
                                  description={resource.description}
                                />
                              ))}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>

                <div className='space-y-4'>
                  <Text className='text-base text-alba-black-500'>
                    Questionário
                  </Text>
                  <div className='space-y-4'>
                    {BUSINESS_QUESTIONNAIRE_ITEMS.map((question, index) => {
                      const isMulti =
                        question.kind === 'fire' || question.kind === 'theft';
                      const answer = formState.questionnaireAnswers[index];
                      return (
                        <BusinessQuestionField
                          key={question.question}
                          question={question.question}
                          kind={question.kind}
                          value={answer?.value ?? (isMulti ? [] : '')}
                          label={answer?.label ?? (isMulti ? [] : '')}
                          error={questionnaireErrors[index]}
                          onValueChange={next =>
                            updateQuestionnaireAnswer(index, next)
                          }
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
              </RequiredTooltipProvider>
            </Dialog.Body>

            <Dialog.Footer className='w-full p-0'>
              <div className='flex w-full items-center justify-end gap-6'>
                <Dialog.CloseTrigger asChild>
                  <Button variant='ghost' size='md'>
                    Cancelar
                  </Button>
                </Dialog.CloseTrigger>
                <Button
                  variant='primary'
                  size='md'
                  loading={isLoading || isLoadingCep}
                  disabled={
                    isLoading ||
                    isLoadingCep ||
                    isCepBlocked ||
                    !formState.activityType ||
                    isLoadingCoverages ||
                    coverages.length === 0
                  }
                  onClick={() => void handleConfirm()}
                >
                  Adicionar
                </Button>
              </div>
            </Dialog.Footer>
          </div>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}

// ─── helpers for EditBusinessAddressModal ──────────────────────────────────

function questionnaireValuesFromLocation(
  location: BusinessLocation
): { value: string | string[]; label: string | string[] }[] {
  return BUSINESS_QUESTIONNAIRE_ITEMS.map((item, index) => {
    const stored = location.questionnaireAnswers[index];
    const isMulti = item.kind === 'fire' || item.kind === 'theft';
    if (isMulti) {
      const arr = Array.isArray(stored)
        ? stored.filter(Boolean)
        : stored
          ? [stored]
          : [];
      return { value: arr, label: [] as string[] };
    }
    const valueStr = Array.isArray(stored) ? (stored[0] ?? '') : (stored ?? '');
    return { value: valueStr, label: '' };
  });
}

/**
 * Reconstrói a franquia personalizada a partir do item estruturado do
 * payload (`policyLimitDeductible`): quando o `minValue`/`name` divergem do
 * item base do catálogo (mesmo código), o usuário personalizou — o
 * percentual é extraído do início do `name` ("15% dos prejuízos...").
 * Retorna `undefined` quando o item espelha o catálogo (sem customização).
 */
function deriveCustomDeductible(
  payload: BusinessLocationCoverage,
  item: Coverage
): CustomDeductible | undefined {
  const structured = payload.policyLimitDeductible?.[0];
  if (!structured) return undefined;

  const baseDetail =
    (item.deductibles ?? []).find(
      deductible => String(deductible.id) === String(structured.id)
    ) ??
    (item.deductibles ?? []).find(
      deductible => String(deductible.code) === String(structured.code)
    );
  if (!baseDetail) return undefined;

  const differsFromBase =
    structured.minValue !== baseDetail.minValue ||
    structured.name !== baseDetail.name;
  if (!differsFromBase) return undefined;

  const percentMatch = /^(\d+)\s*%/.exec(structured.name ?? '');
  if (!percentMatch) return undefined;

  return {
    percentage: percentMatch[1],
    value: structured.minValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }),
  };
}

function deriveCoverageValues(
  coveragesPayload: BusinessLocationCoverage[] | undefined,
  catalog: Coverage[]
): Record<string, CoverageFormValues> {
  if (!coveragesPayload?.length || !catalog.length) return {};
  const result: Record<string, CoverageFormValues> = {};
  coveragesPayload
    .filter(p => !p.planCode && p.value > 0)
    .forEach(payload => {
      const item = catalog.find(
        c => c.code === payload.code || c.id === payload.code
      );
      if (item) {
        const rawDeductible =
          payload.codeDeductible != null ? String(payload.codeDeductible) : '';
        const deductibleOptions = mapDeductibleOptions(item.deductibles ?? []);
        const resolvedFranquia = (() => {
          if (!rawDeductible || rawDeductible === '0') return '';
          // O id do item estruturado identifica a entrada exata do catálogo —
          // necessário quando o mesmo código se repete entre grupos de
          // atividade com descontos diferentes.
          const structuredId = payload.policyLimitDeductible?.[0]?.id;
          // O mesmo código pode se repetir entre faixas de SI (0–5M e 5M–15M):
          // prefere a entrada cuja faixa contém o LMI salvo, senão o card
          // limparia a seleção por estar fora da faixa.
          const matchesByCode = deductibleOptions.filter(
            opt =>
              String(opt.value) === rawDeductible ||
              String(opt.code) === rawDeductible
          );
          const matched =
            (structuredId
              ? deductibleOptions.find(
                  opt => String(opt.id) === String(structuredId)
                )
              : undefined) ??
            matchesByCode.find(opt =>
              isDeductibleOptionValidForLmi(opt, payload.value)
            ) ??
            matchesByCode[0];
          // Fallback: usa o código salvo na policy direto — o Select pré-seleciona
          // quando o catálogo contiver a opção (value === String(code)).
          return matched?.value ?? rawDeductible;
        })();
        const customDeductible = deriveCustomDeductible(payload, item);
        result[item.id] = {
          lmi: payload.value.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }),
          franquia: resolvedFranquia,
          ...(customDeductible ? { customDeductible } : {}),
        };
      }
    });
  return result;
}

function createStateFromLocation(
  location: BusinessLocation
): AddBusinessAddressFormState {
  return {
    cep: location.cep,
    street: location.street,
    number: location.number,
    complement: location.complement,
    neighborhood: location.neighborhood,
    city: location.city,
    state: location.state,
    activityType: location.activityType,
    activityTypeLabel: location.activityTypeLabel ?? '',
    constructionType: location.constructionType,
    isLandmarked: location.isLandmarked,
    hasBusinessInterruption: Boolean(
      location.businessInterruptionValue?.trim()
    ),
    businessInterruptionValue: location.businessInterruptionValue ?? '',
    hasUnderwritingDiscountRequest:
      location.underwritingDiscountRequestInd === 'Y',
    assistancePackage: '',
    questionnaireAnswers: questionnaireValuesFromLocation(location),
    coverageValues: {},
  };
}

// ─── EditBusinessAddressModal ──────────────────────────────────────────────

type EditBusinessAddressModalProps = {
  location: BusinessLocation;
  // eslint-disable-next-line no-unused-vars
  onConfirm: (location: BusinessLocation) => void | Promise<void>;
  constructionTypeOptions: SelectOption[];
  insuredObjectOptions: SelectOption[];
  isLoading?: boolean;
  disabled?: boolean;
  /** Jornada de cotação em análise: permite valores e exibe aviso do fluxo normal. */
  skipCoverageValueValidation?: boolean;
};

export function EditBusinessAddressModal({
  location,
  onConfirm,
  constructionTypeOptions,
  insuredObjectOptions,
  isLoading = false,
  disabled = false,
  skipCoverageValueValidation = false,
}: EditBusinessAddressModalProps) {
  const { id, productCode, productId } = useQuoteParams();
  const { mutateAsync: fetchZipcode, isPending: isLoadingCep } =
    useFetchZipcode();
  const { data: assistancePlans = [], isLoading: isLoadingAssistances } =
    useBusinessAssistances(productId);

  const [open, setOpen] = useState(false);
  const dialogBodyRef = useRef<HTMLDivElement>(null);
  const [formState, setFormState] = useState<AddBusinessAddressFormState>(() =>
    createStateFromLocation(location)
  );
  // Mesmo queryKey do combobox de atividades (cache compartilhado) — usado
  // para trocar códigos de atividade por nome nas mensagens de validação.
  const { data: activityCatalog = [] } = useBusinessActivities();

  // O conteúdo do dialog permanece montado entre aberturas, então o scroll
  // da abertura anterior persiste. Ao abrir, volta a visualização ao topo.
  // O Ark UI só remove o `hidden` do conteúdo de forma assíncrona, então o
  // scrollTo imediato pode rodar com o elemento ainda invisível (no-op) —
  // o double requestAnimationFrame repete o reset após o conteúdo aparecer.
  useEffect(() => {
    if (!open) return;
    const resetScroll = () => dialogBodyRef.current?.scrollTo({ top: 0 });
    resetScroll();
    let innerFrame: number | undefined;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(resetScroll);
    });
    return () => {
      cancelAnimationFrame(outerFrame);
      if (innerFrame !== undefined) cancelAnimationFrame(innerFrame);
    };
  }, [open]);
  const { data: coverages = [], isLoading: isLoadingCoverages } =
    useBusinessCoverages(formState.activityType);
  const { data: planResources = [], isLoading: isLoadingPlanResources } =
    useBusinessPlanResourcesByCode(formState.assistancePackage);

  const assistancePlanOptions: SelectOption[] = assistancePlans.map(plan => ({
    value: plan.code,
    label: plan.name,
  }));

  useEffect(() => {
    if (formState.assistancePackage || assistancePlans.length === 0) return;
    // O pacote persistido chega com o código do plano em
    // `assistancePackageCode` (ex.: `180007SUP`); o rótulo em
    // `assistancePackage` pode ser o próprio código quando o InsureMO não
    // devolve `ProductElementDesc`. Casar primeiro por código evita que a
    // edição reabra no default "Especial" e regrave o plano errado.
    const storedCode = (location.assistancePackageCode ?? '').trim();
    const storedLabel = normalizePlanName(location.assistancePackage ?? '');
    const matchedPlan =
      assistancePlans.find(plan => plan.code === storedCode) ??
      assistancePlans.find(
        plan => normalizePlanName(plan.name) === storedLabel
      ) ??
      assistancePlans.find(
        plan =>
          normalizePlanName(plan.name) ===
          normalizePlanName(DEFAULT_ASSISTANCE_PLAN_NAME)
      ) ??
      assistancePlans[0];
    if (matchedPlan) {
      setFormState(current =>
        current.assistancePackage
          ? current
          : { ...current, assistancePackage: matchedPlan.code }
      );
    }
  }, [
    assistancePlans,
    formState.assistancePackage,
    location.assistancePackage,
    location.assistancePackageCode,
  ]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [questionnaireErrors, setQuestionnaireErrors] = useState<
    Record<number, string>
  >({});
  const [hasResolvedCep, setHasResolvedCep] = useState(
    Boolean(location.city && location.state)
  );
  const [isGenericCep, setIsGenericCep] = useState(
    location.cep.replace(/\D/g, '').endsWith('000')
  );

  /**
   * Travar rua e bairro só faz sentido quando a consulta REALMENTE trouxe um
   * logradouro. `isGenericCep` (CEP terminado em `000`) é um palpite de que o
   * CEP não tem rua — e errado: a Praça da Sé é `01001-000` e tem. Sem olhar o
   * `formState.street`, uma resposta sem rua deixava o campo vazio, travado e
   * obrigatório ao mesmo tempo — beco sem saída para o corretor.
   */
  const canLockStreetAndNeighborhood =
    hasResolvedCep && !isGenericCep && !!String(formState.street || '').trim();
  const [isCepBlocked, setIsCepBlocked] = useState(false);
  const zipcodeLookupCacheRef = useRef<Record<string, boolean>>({});

  const [selectedInsuredObjectType, setSelectedInsuredObjectType] = useState(
    location.insuredObjectType
  );
  const [insuredObjects, setInsuredObjects] = useState<
    { insuredObjectType: string; riskValue: string }[]
  >(
    location.insuredObjectType
      ? [
        {
          insuredObjectType: location.insuredObjectType,
          riskValue: location.riskValue,
        },
      ]
      : []
  );
  const [showInsuredObjectError, setShowInsuredObjectError] = useState(false);
  const [insuredObjectsErrors, setInsuredObjectsErrors] = useState<
    Record<string, string>
  >({});

  const resetToLocation = (loc: BusinessLocation) => {
    const initialCoverageValues = deriveCoverageValues(loc.coveragesPayload, coverages);
    setFormState({ ...createStateFromLocation(loc), coverageValues: initialCoverageValues });
    setErrors({});
    setHasResolvedCep(Boolean(loc.city && loc.state));
    setIsGenericCep(loc.cep.replace(/\D/g, '').endsWith('000'));
    setIsCepBlocked(false);
    zipcodeLookupCacheRef.current = {};
    setSelectedInsuredObjectType(loc.insuredObjectType);
    setInsuredObjects(
      loc.insuredObjectType
        ? [
          {
            insuredObjectType: loc.insuredObjectType,
            riskValue: loc.riskValue,
          },
        ]
        : []
    );
    setShowInsuredObjectError(false);
    setInsuredObjectsErrors({});
  };

  const handleOpenChange = ({ open: nextOpen }: { open: boolean }) => {
    setOpen(nextOpen);
    if (nextOpen) {
      resetToLocation(location);
    } else {
      resetModalState();
    }
  };

  useEffect(() => {
    if (!open || coverages.length === 0) return;
    setFormState(current => {
      if (Object.keys(current.coverageValues).length > 0) return current;
      const values = deriveCoverageValues(location.coveragesPayload, coverages);
      if (!Object.keys(values).length) return current;
      return { ...current, coverageValues: values };
    });
  }, [open, coverages, location.coveragesPayload]);

  const normalize = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const predioOption = insuredObjectOptions.find(
    o => normalize(o.label) === 'predio'
  );
  const conteudoOption = insuredObjectOptions.find(
    o => normalize(o.label) === 'conteudo'
  );
  const combinedOption = insuredObjectOptions.find(
    o => normalize(o.label) === 'predio e conteudo'
  );

  const insuredObjectCardOptions = [
    predioOption && {
      ...predioOption,
      displayLabel: 'Prédio',
      icon: Building2,
    },
    conteudoOption && {
      ...conteudoOption,
      displayLabel: 'Conteúdo',
      icon: Layers,
    },
    combinedOption && {
      ...combinedOption,
      displayLabel: 'Prédio e Conteúdo',
      icon: SplitSquareHorizontal,
    },
  ].filter(Boolean) as Array<{
    label: string;
    value: string;
    displayLabel: string;
    icon: typeof Building2;
  }>;

  const isCombinedInsuredObject = (v: string) => v === combinedOption?.value;
  const isIndividualInsuredObject = (v: string) =>
    v === predioOption?.value || v === conteudoOption?.value;

  const orderInsuredObjects = (
    rows: { insuredObjectType: string; riskValue: string }[]
  ) => {
    const order = insuredObjectCardOptions.map(option => option.value);
    return rows
      .filter(row => String(row.insuredObjectType || '').trim())
      .sort((a, b) => {
        const indexA = order.indexOf(a.insuredObjectType);
        const indexB = order.indexOf(b.insuredObjectType);
        return (
          (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) -
          (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB)
        );
      });
  };

  const selectedRiskValue =
    insuredObjects.find(
      row => row.insuredObjectType === selectedInsuredObjectType
    )?.riskValue || '';

  const updateField = <K extends keyof AddBusinessAddressFormState>(
    key: K,
    value: AddBusinessAddressFormState[K]
  ) => {
    setFormState(current => ({ ...current, [key]: value }));
    setErrors(current => {
      if (!current[key as string]) return current;
      const next = { ...current };
      delete next[key as string];
      return next;
    });
  };

  const updateQuestionnaireAnswer = (
    index: number,
    next: { value: string | string[]; label: string | string[] }
  ) => {
    setFormState(current => ({
      ...current,
      questionnaireAnswers: current.questionnaireAnswers.map((answer, i) =>
        i === index ? next : answer
      ),
    }));
    setQuestionnaireErrors(current => {
      if (current[index] === undefined) return current;
      const nextErrors = { ...current };
      delete nextErrors[index];
      return nextErrors;
    });
  };

  const updateCoverageField = (
    coverageId: string,
    field: 'lmi' | 'franquia',
    value: string
  ) => {
    setFormState(current => ({
      ...current,
      coverageValues: {
        ...current.coverageValues,
        [coverageId]: { ...current.coverageValues[coverageId], [field]: value },
      },
    }));
  };

  const updateCoverageCustomDeductible = (
    coverageId: string,
    value: CustomDeductible | undefined
  ) => {
    setFormState(current => ({
      ...current,
      coverageValues: {
        ...current.coverageValues,
        [coverageId]: {
          ...current.coverageValues[coverageId],
          customDeductible: value,
        },
      },
    }));
  };

  // Lucros Cessantes e Despesas Fixas só podem ser preenchidas após o valor
  // de Lucros Cessantes/Despesas Fixas do local ser informado.
  const hasBusinessInterruptionValue =
    formState.hasBusinessInterruption &&
    parseCoverageCurrency(formState.businessInterruptionValue) > 0;

  useEffect(() => {
    if (hasBusinessInterruptionValue) return;
    setFormState(current =>
      clearBusinessInterruptionCoverageValues(current, coverages)
    );
  }, [hasBusinessInterruptionValue, coverages]);

  const resolveZipcode = async (
    rawCep: string
  ): Promise<{ success: boolean; blocked: boolean }> => {
    const cleanCep = rawCep.replace(/\D/g, '');
    if (cleanCep.length !== 8) return { success: false, blocked: false };

    setIsCepBlocked(false);

    try {
      const result = await fetchZipcode({
        zipcode: cleanCep,
        policyId: id,
        productCode,
      });

      if (result.success && result.data) {
        if ('blocked' in result.data && result.data.blocked) {
          setIsCepBlocked(true);
          toaster.create({
            type: 'error',
            title: 'Risco não aceito',
            description: 'CEP bloqueado para cotação.',
          });
          return { success: false, blocked: true };
        }

        if (!('street' in result.data))
          return { success: false, blocked: false };

        zipcodeLookupCacheRef.current[cleanCep] = true;
        setHasResolvedCep(true);
        setIsGenericCep(cleanCep.endsWith('000'));
        const zipcodeData = result.data;
        setFormState(current => ({
          ...current,
          street: zipcodeData.street || '',
          neighborhood: zipcodeData.neighborhood || '',
          city: zipcodeData.city || '',
          state: zipcodeData.state || '',
        }));
        return { success: true, blocked: false };
      }

      zipcodeLookupCacheRef.current[cleanCep] = false;
      setHasResolvedCep(false);
      setIsGenericCep(false);
      toaster.create({
        type: 'error',
        title: 'CEP não encontrado',
        description: 'Verifique o CEP informado e tente novamente.',
      });
      return { success: false, blocked: false };
    } catch (error) {
      zipcodeLookupCacheRef.current[cleanCep] = false;
      setHasResolvedCep(false);
      setIsGenericCep(false);
      toaster.create({
        type: 'error',
        title: 'Erro ao buscar CEP',
        description:
          error instanceof Error && error.message.trim()
            ? error.message
            : 'Verifique o CEP informado e tente novamente.',
      });
      return { success: false, blocked: false };
    }
  };

  const [coverageErrors, setCoverageErrors] = useState<Record<string, string>>(
    {}
  );

  const resetModalState = () => {
    setFormState(createEmptyState());
    setErrors({});
    setCoverageErrors({});
    setQuestionnaireErrors({});
    setSelectedInsuredObjectType('');
    setInsuredObjects([]);
    setShowInsuredObjectError(false);
    setInsuredObjectsErrors({});
    setHasResolvedCep(false);
    setIsGenericCep(false);
    setIsCepBlocked(false);
    zipcodeLookupCacheRef.current = {};
  };

  const buildDeductibleOptionsByCoverageId = () =>
    Object.fromEntries(
      coverages.map(coverage => [
        coverage.id,
        mapDeductibleOptions(coverage.deductibles ?? []),
      ])
    );

  const validateCoverages = (): boolean => {
    if (skipCoverageValueValidation) {
      setCoverageErrors({});
      return true;
    }

    const newErrors = collectNormalFlowCoverageBlockingMessages(
      coverages,
      formState.coverageValues,
      selectedRiskValue,
      buildDeductibleOptionsByCoverageId()
    );

    setCoverageErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstError = Object.values(newErrors)[0];
      toaster.create({
        type: 'error',
        title: 'Erro nas coberturas',
        description: firstError,
      });
      return false;
    }

    return true;
  };

  const handleConfirm = async () => {
    if (isLoading || isLoadingCep || isCepBlocked) return;

    const fieldValidationErrors = validateRequiredFields(
      formState,
      selectedInsuredObjectType,
      selectedRiskValue
    );

    if (!selectedInsuredObjectType) {
      setShowInsuredObjectError(true);
    }

    const questionnaireValidationErrors = validateQuestionnaire(formState);

    if (
      Object.keys(fieldValidationErrors).length > 0 ||
      Object.keys(questionnaireValidationErrors).length > 0
    ) {
      setErrors(fieldValidationErrors);
      setQuestionnaireErrors(questionnaireValidationErrors);
      toaster.create({
        type: 'error',
        title: 'Campos obrigatórios',
        description: 'Preencha todos os campos obrigatórios para continuar.',
      });
      return;
    }

    const cleanCep = formState.cep.replace(/\D/g, '');
    if (
      cleanCep.length === 8 &&
      zipcodeLookupCacheRef.current[cleanCep] !== true
    ) {
      const { blocked } = await resolveZipcode(cleanCep);
      if (blocked) return;
    }

    if (!validateCoverages()) return;

    const activityTypeLabel =
      formState.activityTypeLabel || formState.activityType;
    const constructionTypeLabel =
      constructionTypeOptions.find(
        option => option.value === formState.constructionType
      )?.label ?? formState.constructionType;
    const insuredObjectTypeLabel =
      insuredObjectOptions.find(
        option => option.value === selectedInsuredObjectType
      )?.label ?? selectedInsuredObjectType;
    const assistancePackage =
      assistancePlans.find(plan => plan.code === formState.assistancePackage)
        ?.name ?? formState.assistancePackage;
    const questionnaireAnswers: (string | string[])[] =
      formState.questionnaireAnswers.map((answer, index) => {
        const item = BUSINESS_QUESTIONNAIRE_ITEMS[index];
        const isMulti = item?.kind === 'fire' || item?.kind === 'theft';
        if (isMulti) {
          const valueArr = Array.isArray(answer.value) ? answer.value : [];
          return valueArr;
        }
        const valueStr = typeof answer.value === 'string' ? answer.value : '';
        return valueStr;
      });

    const { coveragesPayload: assistanceCoverages, benefitsPayload } =
      buildAssistancePayloads({
        productCode,
        planCode: formState.assistancePackage,
        planResources,
      });
    const regularCoverages = buildRegularCoveragesPayload(
      coverages,
      formState.coverageValues
    );
    const coveragesPayload = [...regularCoverages, ...assistanceCoverages];

    try {
      await onConfirm({
        id: location.id,
        riskPk: location.riskPk,
        cep: formState.cep.trim(),
        street: formState.street.trim(),
        number: formState.number.trim(),
        complement: formState.complement.trim(),
        neighborhood: formState.neighborhood.trim(),
        city: formState.city.trim(),
        state: formState.state.trim(),
        activityType: formState.activityType,
        activityTypeLabel,
        constructionType: formState.constructionType,
        constructionTypeLabel,
        isLandmarked: formState.isLandmarked,
        insuredObjectType: selectedInsuredObjectType,
        insuredObjectTypeLabel,
        riskValue: selectedRiskValue.trim(),
        businessInterruptionValue: formState.hasBusinessInterruption
          ? formState.businessInterruptionValue.trim()
          : '',
        underwritingDiscountRequestInd: formState.hasUnderwritingDiscountRequest
          ? 'Y'
          : 'N',
        assistancePackage,
        assistancePackageCode: formState.assistancePackage,
        questionnaireAnswers,
        coveragesPayload,
        benefitsPayload,
        propertyType: location.propertyType,
        propertyClassification: location.propertyClassification,
      });

      toaster.create({
        type: 'success',
        title: 'Local atualizado',
        description: 'O local de risco foi atualizado com sucesso.',
      });
      setOpen(false);
    } catch (error) {
      const isQuoteClosed =
        error instanceof ValidationError &&
        error.fieldErrors?.quoteStatus?.code === '2';

      if (isQuoteClosed) {
        setOpen(false);
        return;
      }

      if (error instanceof ValidationError && error.fieldErrors) {
        const { coverageErrors: mappedErrors, coverageNames } =
          mapValidationErrorToCoverageErrors(error, coverages);
        if (Object.keys(mappedErrors).length > 0) {
          setCoverageErrors(prev => ({ ...prev, ...mappedErrors }));
          toaster.create({
            type: 'error',
            title: 'Erro de validação',
            description: `Erro nas coberturas: ${coverageNames.join(', ')}`,
          });
          return;
        }
      }

      toaster.create({
        type: 'error',
        title: 'Erro ao atualizar local',
        description:
          error instanceof Error
            ? replaceActivityCodesWithNames(error.message, [
                ...activityCatalog,
                // Fallback: a atividade recém-selecionada pode não estar na
                // página carregada do catálogo (busca paginada/filtrada).
                {
                  code: Number(formState.activityType),
                  description: formState.activityTypeLabel,
                },
              ])
            : 'Não foi possível atualizar o local de risco.',
      });
    }
  };

  return (
    <Dialog.Root
      open={open}
      closeOnInteractOutside={false}
      closeOnEscape={false}
      onOpenChange={handleOpenChange}
    >
      <Dialog.Trigger asChild>
        <button
          type='button'
          disabled={disabled}
          className='rounded-lg p-2 text-alba-blue-500 transition hover:bg-alba-gray-200 disabled:cursor-not-allowed disabled:opacity-50'
        >
          <Pencil className='h-4 w-4' strokeWidth={1.6} />
        </button>
      </Dialog.Trigger>

      <Dialog.Backdrop />

      <Dialog.Positioner>
        <Dialog.Content className='w-[920px] max-w-[95vw] rounded-[24px] bg-alba-white-500 p-0 shadow-xl'>
          <div className='flex max-h-[90vh] flex-col gap-8 p-6'>
            <Dialog.Header className='w-full p-0'>
              <Dialog.Title className='w-full'>
                <div className='flex w-full items-center justify-between gap-3'>
                  <div className='flex items-center gap-2'>
                    <Pencil className='h-6 w-6 text-alba-black-500' />
                    <Text className='text-lg font-bold text-alba-black-500'>
                      Editar Local
                    </Text>
                  </div>
                  <Dialog.CloseTrigger asChild>
                    <Dialog.CloseButton />
                  </Dialog.CloseTrigger>
                </div>
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body
              ref={dialogBodyRef}
              className='w-full flex-1 overflow-y-auto p-0 pr-2'
            >
              <RequiredTooltipProvider restartKey={String(open)} introMs={4000}>
              <div className='flex flex-col gap-8'>
                <div className='space-y-4'>
                  <Text className='text-base text-alba-black-500'>
                    Dados do Local
                  </Text>

                  <div className='space-y-4'>
                    <div className='lg:w-1/2'>
                      <RequiredFieldTooltip active>
                      <Field.Root invalid={Boolean(errors.cep)}>
                        <Field.Label className='text-sm font-bold text-alba-black-500'>
                          CEP
                          <span className='text-alba-error-red-500'>*</span>
                        </Field.Label>
                        <Field.Input
                          value={formState.cep}
                          onChange={event =>
                            updateField(
                              'cep',
                              formatZipcode(event.target.value)
                            )
                          }
                          onBlur={() => void resolveZipcode(formState.cep)}
                          placeholder='XXXXX-XXX'
                          maxLength={FIELD_MAX_LENGTHS.cep}
                          className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600'
                          disabled={isLoadingCep}
                        />
                        {errors.cep && (
                          <Field.ErrorText>{errors.cep}</Field.ErrorText>
                        )}
                      </Field.Root>
                      </RequiredFieldTooltip>
                    </div>

                    <RequiredFieldTooltip active>
                    <Field.Root invalid={Boolean(errors.street)}>
                      <Field.Label className='text-sm font-bold text-alba-black-500'>
                        Endereço
                        <span className='text-alba-error-red-500'>*</span>
                      </Field.Label>
                      <Field.Input
                        value={formState.street}
                        onChange={event =>
                          updateField('street', event.target.value)
                        }
                        placeholder='Rua, Avenida ...'
                        maxLength={FIELD_MAX_LENGTHS.street}
                        className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600'
                        disabled={canLockStreetAndNeighborhood}
                      />
                      {errors.street && (
                        <Field.ErrorText>{errors.street}</Field.ErrorText>
                      )}
                    </Field.Root>
                    </RequiredFieldTooltip>

                    <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
                      <RequiredFieldTooltip active>
                      <Field.Root invalid={Boolean(errors.number)}>
                        <Field.Label className='text-sm font-bold text-alba-black-500'>
                          Número
                          <span className='text-alba-error-red-500'>*</span>
                        </Field.Label>
                        <Field.Input
                          value={formState.number}
                          onChange={event =>
                            updateField('number', event.target.value)
                          }
                          placeholder='Número'
                          maxLength={FIELD_MAX_LENGTHS.addressNumber}
                          className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600'
                        />
                        {errors.number && (
                          <Field.ErrorText>{errors.number}</Field.ErrorText>
                        )}
                      </Field.Root>
                      </RequiredFieldTooltip>

                      <Field.Root>
                        <Field.Label className='text-sm font-bold text-alba-black-500'>
                          Complemento
                        </Field.Label>
                        <Field.Input
                          value={formState.complement}
                          onChange={event =>
                            updateField('complement', event.target.value)
                          }
                          placeholder='Apto, Bloco ...'
                          maxLength={FIELD_MAX_LENGTHS.complement}
                          className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600'
                        />
                      </Field.Root>
                    </div>

                    <div className='grid grid-cols-1 gap-4 lg:grid-cols-3'>
                      <RequiredFieldTooltip active>
                      <Field.Root invalid={Boolean(errors.neighborhood)}>
                        <Field.Label className='text-sm font-bold text-alba-black-500'>
                          Bairro
                          <span className='text-alba-error-red-500'>*</span>
                        </Field.Label>
                        <Field.Input
                          value={formState.neighborhood}
                          onChange={event =>
                            updateField('neighborhood', event.target.value)
                          }
                          placeholder='Bairro'
                          maxLength={FIELD_MAX_LENGTHS.neighborhood}
                          className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600'
                          disabled={canLockStreetAndNeighborhood}
                        />
                        {errors.neighborhood && (
                          <Field.ErrorText>
                            {errors.neighborhood}
                          </Field.ErrorText>
                        )}
                      </Field.Root>
                      </RequiredFieldTooltip>

                      <RequiredFieldTooltip active>
                      <Field.Root invalid={Boolean(errors.city)}>
                        <Field.Label className='text-sm font-bold text-alba-black-500'>
                          Cidade
                          <span className='text-alba-error-red-500'>*</span>
                        </Field.Label>
                        <Field.Input
                          value={formState.city}
                          onChange={event =>
                            updateField('city', event.target.value)
                          }
                          placeholder='Cidade'
                          className='h-10 w-full rounded-xl border border-alba-gray-400 bg-[#E7E8E9] px-5 text-base text-alba-gray-600'
                          disabled
                        />
                        {errors.city && (
                          <Field.ErrorText>{errors.city}</Field.ErrorText>
                        )}
                      </Field.Root>
                      </RequiredFieldTooltip>

                      <SelectField
                        label='Estado'
                        required
                        placeholder='UF'
                        value={formState.state}
                        options={STATE_OPTIONS}
                        onValueChange={value => updateField('state', value)}
                        error={errors.state}
                        disabled
                      />
                    </div>
                  </div>
                </div>

                <div className='space-y-4'>
                  <Text className='text-base text-alba-black-500'>
                    Dados do Risco
                  </Text>

                  <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
                    <BusinessActivityField
                      value={formState.activityType}
                      onValueChange={({ value, label }) => {
                        setFormState(current => ({
                          ...current,
                          activityType: value,
                          activityTypeLabel: label,
                        }));
                        setErrors(current => {
                          if (!current.activityType) return current;
                          const next = { ...current };
                          delete next.activityType;
                          return next;
                        });
                      }}
                      fallbackLabel={formState.activityTypeLabel}
                      error={errors.activityType}
                    />

                    <SelectField
                      label='Tipo de Construção'
                      required
                      value={formState.constructionType}
                      options={constructionTypeOptions}
                      onValueChange={value =>
                        updateField('constructionType', value)
                      }
                      error={errors.constructionType}
                    />
                  </div>

                  <Checkbox.Root
                    checked={formState.isLandmarked}
                    onCheckedChange={() => {}}
                    onClick={() =>
                      updateField('isLandmarked', !formState.isLandmarked)
                    }
                    className='flex items-center gap-3'
                  >
                    <Checkbox.Control className='h-5 w-5 cursor-pointer'>
                      <Checkbox.Indicator />
                    </Checkbox.Control>
                    <Checkbox.Label className='cursor-pointer text-sm text-alba-black-500'>
                      Imóvel Tombado?
                    </Checkbox.Label>
                  </Checkbox.Root>

                  <div className='space-y-4'>
                    <RequiredFieldTooltip active>
                    <div className='space-y-4 sm:space-y-2'>
                      <label className='block text-sm font-bold text-alba-black-500'>
                        Objeto Segurado
                        <span className='text-alba-error-red-500'>*</span>
                      </label>
                      <RadioCard.Root
                        value={selectedInsuredObjectType}
                        onValueChange={details => {
                          const nextValue = String(details.value || '').trim();
                          setSelectedInsuredObjectType(nextValue);
                          setShowInsuredObjectError(false);
                          setInsuredObjectsErrors({});
                          setErrors(current => {
                            const next = { ...current };
                            delete next.insuredObjectType;
                            delete next.riskValue;
                            return next;
                          });
                          setInsuredObjects(prev => {
                            if (isCombinedInsuredObject(nextValue)) {
                              return prev.filter(
                                row =>
                                  isCombinedInsuredObject(
                                    String(row.insuredObjectType || '').trim()
                                  ) && String(row.riskValue || '').trim()
                              );
                            }
                            return orderInsuredObjects(
                              prev.filter(row =>
                                isIndividualInsuredObject(
                                  String(row.insuredObjectType || '').trim()
                                )
                              )
                            );
                          });
                        }}
                      >
                        <div className='flex flex-col gap-2 sm:flex-row'>
                          {insuredObjectCardOptions.map(option => {
                            const Icon = option.icon;
                            return (
                              <RadioCard.Item
                                key={option.value}
                                value={option.value}
                                className='h-auto w-full rounded-xl sm:h-[120px] sm:w-52'
                              >
                                <RadioCard.ItemControl className='relative flex h-full flex-col items-start justify-center px-4'>
                                  <div className='absolute right-3 top-3'>
                                    <RadioCard.RadioButton />
                                  </div>
                                  <div className='flex flex-row items-center justify-start gap-3 py-3 sm:flex-col sm:items-start sm:gap-2 sm:py-0'>
                                    <Icon
                                      className='h-6 w-6 sm:h-8 sm:w-8'
                                      strokeWidth={1.6}
                                    />
                                    <RadioCard.ItemText className='whitespace-nowrap text-left'>
                                      {option.displayLabel}
                                    </RadioCard.ItemText>
                                  </div>
                                </RadioCard.ItemControl>
                                <RadioCard.ItemHiddenInput />
                              </RadioCard.Item>
                            );
                          })}
                        </div>
                      </RadioCard.Root>
                      {showInsuredObjectError && (
                        <Field.Root invalid>
                          <Field.ErrorText>
                            Selecione o objeto segurado
                          </Field.ErrorText>
                        </Field.Root>
                      )}
                    </div>
                    </RequiredFieldTooltip>

                    {selectedInsuredObjectType && (
                      <RequiredFieldTooltip active>
                      <Field.Root
                        invalid={
                          !!insuredObjectsErrors[selectedInsuredObjectType] ||
                          !!errors.riskValue
                        }
                        required
                      >
                        <Field.Label>
                          Risco declarado
                          <span className='text-alba-error-red-500'>*</span>
                        </Field.Label>
                        <Field.Input
                          value={selectedRiskValue}
                          onChange={e => {
                            const value = formatCurrencyFromString(
                              e.target.value
                            );
                            setInsuredObjectsErrors(prev => {
                              if (!prev[selectedInsuredObjectType]) return prev;
                              const next = { ...prev };
                              delete next[selectedInsuredObjectType];
                              return next;
                            });
                            setErrors(current => {
                              if (!current.riskValue) return current;
                              const next = { ...current };
                              delete next.riskValue;
                              return next;
                            });
                            setInsuredObjects(prev => {
                              const next = prev.filter(
                                row =>
                                  row.insuredObjectType !==
                                  selectedInsuredObjectType
                              );
                              if (String(value || '').trim()) {
                                next.push({
                                  insuredObjectType: selectedInsuredObjectType,
                                  riskValue: value,
                                });
                              }
                              return orderInsuredObjects(next);
                            });
                          }}
                          placeholder='R$ 0,00'
                          className='h-10 w-full rounded-xl border border-alba-gray-400 bg-white px-5 text-base text-alba-gray-600'
                        />
                        {(insuredObjectsErrors[selectedInsuredObjectType] ||
                          errors.riskValue) && (
                          <Field.ErrorText>
                            {insuredObjectsErrors[selectedInsuredObjectType] ||
                              errors.riskValue}
                          </Field.ErrorText>
                        )}
                      </Field.Root>
                      </RequiredFieldTooltip>
                    )}

                    <BusinessInterruptionFields
                      checked={formState.hasBusinessInterruption}
                      value={formState.businessInterruptionValue}
                      onCheckedChange={next =>
                        setFormState(current => ({
                          ...current,
                          hasBusinessInterruption: next,
                          businessInterruptionValue: next
                            ? current.businessInterruptionValue
                            : '',
                        }))
                      }
                      onValueChange={value =>
                        updateField('businessInterruptionValue', value)
                      }
                    />
                  </div>
                </div>

                <div className='space-y-4'>
                  <Text className='text-base text-alba-black-500'>
                    Coberturas
                  </Text>
                  <div className='space-y-3'>
                    {!formState.activityType ? (
                      <div className='rounded-xl border border-dashed border-alba-gray-400 bg-alba-gray-100 px-4 py-6 text-center'>
                        <Text className='text-sm text-alba-gray-600'>
                          Selecione o tipo de atividade realizada para visualizar
                          as coberturas disponíveis.
                        </Text>
                      </div>
                    ) : isLoadingCoverages ? (
                      <Text className='text-sm text-alba-gray-600'>
                        Carregando coberturas...
                      </Text>
                    ) : coverages.length === 0 ? (
                      <div className='rounded-xl border border-dashed border-alba-gray-400 bg-alba-gray-100 px-4 py-6 text-center'>
                        <Text className='text-sm text-alba-gray-600'>
                          Não há coberturas disponíveis para o tipo de atividade
                          selecionado.
                        </Text>
                      </div>
                    ) : (
                      coverages.map(coverage => (
                        <CoverageCard
                          key={coverage.id}
                          coverage={{
                            id: coverage.id,
                            code: coverage.code,
                            title: coverage.name,
                            description: coverage.description ?? '',
                            required: coverage.required,
                            limit: {
                              type: 'range',
                              constraints: {
                                min: coverage.limit?.constraints?.min ?? 0,
                                max:
                                  coverage.limit?.constraints?.max ??
                                  Number.MAX_SAFE_INTEGER,
                              },
                            },
                            limitValue:
                              formState.coverageValues[coverage.id]?.lmi ?? '',
                            deductible:
                              formState.coverageValues[coverage.id]?.franquia ??
                              '',
                            deductibleOptions: mapDeductibleOptions(
                              coverage.deductibles ?? []
                            ),
                            onLimitChange: value => {
                              updateCoverageField(coverage.id, 'lmi', value);
                              if (coverageErrors[coverage.id]) {
                                setCoverageErrors(prev => {
                                  const next = { ...prev };
                                  delete next[coverage.id];
                                  return next;
                                });
                              }
                            },
                            onDeductibleChange: value =>
                              updateCoverageField(
                                coverage.id,
                                'franquia',
                                value
                              ),
                            allowCustomDeductible: true,
                            customDeductible:
                              formState.coverageValues[coverage.id]
                                ?.customDeductible,
                            onCustomDeductibleChange: value =>
                              updateCoverageCustomDeductible(
                                coverage.id,
                                value
                              ),
                            disabled:
                              isBusinessInterruptionCoverage(coverage.name) &&
                              !hasBusinessInterruptionValue,
                            validationError: coverageErrors[coverage.id],
                            skipValueValidation: skipCoverageValueValidation,
                            riskValueLimit: parseCurrencyLikeToNumber(
                              selectedRiskValue
                            ),
                            percentOfBasicLimit: coverage.limit?.percent,
                            basicCoverageLmi: getBasicCoverageLmi(
                              coverages,
                              formState.coverageValues
                            ),
                          }}
                        />
                      ))
                    )}
                  </div>
                </div>

                <div className='space-y-4'>
                  <Text className='text-base text-alba-black-500'>
                    Assistências
                  </Text>
                  {isLoadingAssistances ? (
                    <Text className='text-sm text-alba-gray-600'>
                      Carregando pacotes...
                    </Text>
                  ) : (
                    <SelectField
                      label='Pacote'
                      placeholder='Selecione o pacote'
                      value={formState.assistancePackage}
                      options={assistancePlanOptions}
                      onValueChange={value =>
                        updateField('assistancePackage', value)
                      }
                    />
                  )}

                  {formState.assistancePackage && (
                    <div className='flex flex-col gap-3'>
                      {isLoadingPlanResources ? (
                        <Text className='text-sm text-alba-gray-600'>
                          Carregando benefícios do pacote...
                        </Text>
                      ) : (
                        planResources.length > 0 && (
                          <div className='grid grid-cols-1 gap-3 lg:grid-cols-2'>
                            {planResources
                              .filter(isAssistanceBenefitResource)
                              .map(resource => (
                                <AssistancePackageCard
                                  key={resource.code}
                                  title={resource.name}
                                  description={resource.description}
                                />
                              ))}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>

                <div className='space-y-4'>
                  <Text className='text-base text-alba-black-500'>
                    Questionário
                  </Text>
                  <div className='space-y-4'>
                    {BUSINESS_QUESTIONNAIRE_ITEMS.map((question, index) => {
                      const isMulti =
                        question.kind === 'fire' || question.kind === 'theft';
                      const answer = formState.questionnaireAnswers[index];
                      return (
                        <BusinessQuestionField
                          key={question.question}
                          question={question.question}
                          kind={question.kind}
                          value={answer?.value ?? (isMulti ? [] : '')}
                          label={answer?.label ?? (isMulti ? [] : '')}
                          error={questionnaireErrors[index]}
                          onValueChange={next =>
                            updateQuestionnaireAnswer(index, next)
                          }
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
              </RequiredTooltipProvider>
            </Dialog.Body>

            <Dialog.Footer className='w-full p-0'>
              <div className='flex w-full items-center justify-end gap-6'>
                <Dialog.CloseTrigger asChild>
                  <Button variant='ghost' size='md'>
                    Cancelar
                  </Button>
                </Dialog.CloseTrigger>
                <Button
                  variant='primary'
                  size='md'
                  loading={isLoading || isLoadingCep}
                  disabled={
                    isLoading ||
                    isLoadingCep ||
                    isCepBlocked ||
                    !formState.activityType ||
                    isLoadingCoverages ||
                    coverages.length === 0
                  }
                  onClick={() => void handleConfirm()}
                >
                  Salvar
                </Button>
              </div>
            </Dialog.Footer>
          </div>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
